import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { startSpan, endSpan } from '../../telemetry/telemetry';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  //Divider,
  AppBar,
  Toolbar,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  OutlinedInput,
  InputLabel,
  Tab,
  Tabs,
  ListItemIcon,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Translate as TranslateIcon,
  CheckCircle as CheckCircleIcon,
  FactCheck as FactCheckIcon,
  WarningAmber as WarningAmberIcon,
  KeyboardArrowDown as ArrowDropDownIcon,
} from '@mui/icons-material';

import { Document, Packer, Paragraph as DocxParagraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType, AlignmentType } from 'docx';
import * as docx from 'docx'; // Ensure docx is imported correctly
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { extractPlainTextForPdf, normalizePdfText } from '../../utils/formattingUtils';
import { getPredefinedICFSections } from '../constants/icfSectionData';
import {
  getICFTitlePageDefinition,
  getTitlePageBasePrompt,
  isICFTitlePageSection,
} from '../constants/icfTitlePageData';
import { fetchICFTitlePageContext } from '../utils/fetchICFTitlePageContext';
import {
  buildIcfSectionGenerateContext,
  buildIcfUserEditGenerationPrompt,
} from '../utils/icfSectionGenerateContext';
import { sortIcfSectionsNaturally } from '../utils/icfSectionSort';
import { getLatestIcfVersionLabel } from '../utils/icfVersionUtils';
import { generateIcfContentViaJob } from '../utils/generateIcfContentViaJob';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import documentEditorService from '../../services/documentEditor.service';
import feasibilityService from '../../services/feasibility.service';
import { useProject } from '../../context/ProjectContext';
import ScrollToTop from '../../components/common/ScrollToTop';
import StyleComplianceCheck from '../../components/common/StyleComplianceCheck';
import { parseTableCaptionLine } from '../../utils/tableTextFormat';
import { colors as dizzarooColors } from '../../theme/tokens';

// Helper functions for XML export
function toCamelCase(str) {
  if (!str) return 'section';
  // Remove any characters that are invalid in XML tag names
  return str
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/(?:^|\s)([a-z])/g, (_, char) => char.toUpperCase()) // Capitalize first letter after space
    .replace(/\s+/g, '') // Remove all spaces
    .replace(/^(.)/, char => char.toLowerCase()) // Ensure first character is lowercase
    .replace(/^[^a-z]/i, 'section'); // If starts with non-letter, use 'section'
}

function parseSectionContent(content, mainNum) {
  const subsections = [];
  const subsectionRegex = new RegExp(`^${mainNum}\\.\\d+\\s+(.+)\\n([\\s\\S]*?)(?=\\n${mainNum}\\.\\d+\\s+|$)
`, 'gm');
  let match;
  let lastIndex = 0;
  let mainContent = '';

  while ((match = subsectionRegex.exec(content)) !== null) {
    // Add the content before this subsection to mainContent
    mainContent += content.substring(lastIndex, match.index);

    subsections.push({
      number: match[0].split(' ')[0],
      title: (match[1] || '').trim(),
      content: (match[2] || '').trim(),
    });
    lastIndex = subsectionRegex.lastIndex;
  }

  // Add any remaining content after the last subsection to mainContent
  mainContent += content.substring(lastIndex);

  return { main: (mainContent || '').trim(), subsections };
}

// Safely escape XML entities in text nodes
function escapeXml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Safely escape HTML entities in text nodes
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function jsonToXml(obj, tagName, indent = '') {
  let xml = `${indent}<${tagName}>\n`;
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (Array.isArray(value)) {
        for (const item of value) {
          xml += jsonToXml(item, key.slice(0, -1) || key, indent + '  '); // Use singular tag name for array items
        }
      } else if (typeof value === 'object' && value !== null) {
        xml += jsonToXml(value, key, indent + '  ');
      } else {
        xml += `${indent}  <${key}>${escapeXml(value)}</${key}>\n`;
      }
    }
  }
  xml += `${indent}</${tagName}>`;
  return xml;
}

// Dizzaroo brand theme – Primary: Deep Blue #1E73BE
const theme = createTheme({
  palette: {
    primary: {
      main: dizzarooColors.primary[500],
      light: dizzarooColors.primary.light,
      dark: dizzarooColors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: dizzarooColors.accent.teal,
      light: dizzarooColors.accent.tealLight,
      dark: dizzarooColors.accent.tealDark,
      contrastText: '#ffffff',
    },
    background: { default: '#f6fbff', paper: '#ffffff' },
    text: { primary: '#1b2a3a', secondary: '#6b7a90' },
  },
  typography: {
    fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    subtitle2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    body1: { fontFamily: '"Urbanist", sans-serif' },
    body2: { fontFamily: '"Urbanist", sans-serif' },
    button: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&display=swap');
      `,
    },
    MuiButton: {
      defaultProps: { color: 'primary', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 },
        contained: {
          '&.MuiButton-colorPrimary': {
            backgroundColor: dizzarooColors.primary[500],
            '&:hover': { backgroundColor: dizzarooColors.primary[600] },
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 6px 24px rgba(13, 88, 131, 0.08)', borderRadius: 14 } } },
    MuiAppBar: { styleOverrides: { root: { borderBottomLeftRadius: 12, borderBottomRightRadius: 12 } } },
  },
});

const SUPPORTED_TRANSLATION_LANGUAGES = [
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Japanese',
  'Chinese (Simplified)',
  'Chinese (Traditional)',
  'Korean',
];

const ICFEditor = ({
  onSubmit,
  initialData,
  loading,
  projectId: projectIdProp,
  documentId: documentIdProp,
}) => { 
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId') || queryParams.get('project'); // Support both 'projectId' and 'project' query params
  const selectedCountryRaw = queryParams.get('country');
  const selectedCountry = selectedCountryRaw ? decodeURIComponent(selectedCountryRaw) : null;
  const selectedSiteRaw = queryParams.get('site');
  const selectedSite = selectedSiteRaw ? decodeURIComponent(selectedSiteRaw) : null;
  const selectedLanguageRaw = queryParams.get('language');
  const selectedLanguage = selectedLanguageRaw ? decodeURIComponent(selectedLanguageRaw) : null;
  const selectedVersionRaw = queryParams.get('version');
  const selectedVersion = selectedVersionRaw ? decodeURIComponent(selectedVersionRaw) : null;
  const fromApprovedTranslation = queryParams.get('fromApproved') === 'true'; // Track if opened from approved translation
  const forReview = queryParams.get('forReview') === 'true'; // Track if opened from translations tab for review

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } =
    useProject();

  const documentIdFromParams = params?.id;
  const contextProjectId = currentProject?._id;
  const effectiveProjectId = contextProjectId || projectIdProp || projectIdFromUrl;
  const resolvedDocumentId = documentIdFromParams || documentIdProp || effectiveProjectId;

  useEffect(() => {
    let mounted = true;
    const loadSelectedCountries = async () => {
      if (!effectiveProjectId) return;
      try {
        const report = await feasibilityService.getCountryReport(effectiveProjectId);
        const selected = Array.isArray(report?.selectedCountries) ? report.selectedCountries : [];
        if (!mounted) return;
        setCountrySelectionOptions(selected);
        if (selected.length) {
          setCountryNameMode('selected');
          setNewCountryName((prev) => prev || selected[0] || '');
        } else {
          setCountryNameMode('custom');
        }
      } catch (_err) {
        if (!mounted) return;
        setCountrySelectionOptions([]);
        setCountryNameMode('custom');
      }
    };
    loadSelectedCountries();
    return () => {
      mounted = false;
    };
  }, [effectiveProjectId]);

  const [sections, setSections] = useState([]);
  const icfContentSections = useMemo(
    () => sections.filter((section) => !isICFTitlePageSection(section)),
    [sections]
  );
  const titlePageSection = useMemo(
    () => sections.find((section) => isICFTitlePageSection(section)) || null,
    [sections]
  );
  const [translatedSections, setTranslatedSections] = useState([]); // Translated sections when language is selected
  const [isTranslating, setIsTranslating] = useState(false);
  const [retranslatedSections, setRetranslatedSections] = useState({}); // Re-translated back to English: { sectionId: { text, accuracy, accuracyLLM, accuracyRuleBased, explanation, keyDifferences, method } }
  const [isRetranslating, setIsRetranslating] = useState({}); // Track which sections are being re-translated
  const [showAccuracy, setShowAccuracy] = useState({}); // Toggle to show/hide accuracy for each section: { sectionId: true/false }
  const [showRetranslatedText, setShowRetranslatedText] = useState({}); // Toggle to show retranslated text vs language version: { sectionId: true/false }
  const [accuracyMethod, setAccuracyMethod] = useState({}); // Accuracy calculation method for each section: { sectionId: 'llm' | 'rule-based' }
  const [highlightedRetranslatedHtml, setHighlightedRetranslatedHtml] = useState({}); // Cached highlighted HTML: { sectionId: htmlString }
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isGenerating, setIsGenerating] = useState({});
  // Per-section user editing instructions (not persisted until Generate Content)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableSectionTitles, setAvailableSectionTitles] = useState([]);
  const [mode, setMode] = useState('preview'); // 'edit' or 'preview'
  const [editedTextContent, setEditedTextContent] = useState({}); // Track edited text content
  const [editedTranslationContent, setEditedTranslationContent] = useState({}); // Track edited translation content for review
  const [mainPageTab, setMainPageTab] = useState(0);

  /** Match translated panel scroll height to the English preview box per section. */
  const englishPreviewHeightRefs = useRef({});
  const [englishPreviewHeights, setEnglishPreviewHeights] = useState({});

  const registerEnglishPreviewBox = useCallback((sectionId, node) => {
    if (!sectionId) return;
    const prev = englishPreviewHeightRefs.current[sectionId];
    if (prev?.node === node) return;
    if (prev?.observer) prev.observer.disconnect();
    if (!node) {
      delete englishPreviewHeightRefs.current[sectionId];
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.contentRect?.height || 0);
      if (height > 0) {
        setEnglishPreviewHeights((old) =>
          old[sectionId] === height ? old : { ...old, [sectionId]: height },
        );
      }
    });
    observer.observe(node);
    englishPreviewHeightRefs.current[sectionId] = { node, observer };
  }, []);

  useEffect(
    () => () => {
      Object.values(englishPreviewHeightRefs.current).forEach(({ observer }) => observer?.disconnect());
      englishPreviewHeightRefs.current = {};
    },
    [],
  );

  const getTranslatedPanelHeightSx = useCallback(
    (sectionId) => {
      const h = englishPreviewHeights[sectionId];
      if (!h || h <= 0) return { minHeight: 200 };
      return { height: h, minHeight: h, maxHeight: h };
    },
    [englishPreviewHeights],
  );

  const translatedTextFieldSx = useCallback(
    (sectionId) => ({
      mt: 1,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      '& .MuiInputBase-root': {
        flex: 1,
        alignItems: 'stretch',
        ...getTranslatedPanelHeightSx(sectionId),
      },
      '& textarea': {
        height: '100% !important',
        overflowY: 'auto !important',
        boxSizing: 'border-box',
      },
    }),
    [getTranslatedPanelHeightSx],
  );

  // KPI: user-perceived editor load (route open → sections ready)
  useEffect(() => {
    startSpan('ui_icf_editor_load', {
      page: 'icf_editor',
      meta: { projectId: effectiveProjectId || null, country: selectedCountry || null, site: selectedSite || null, language: selectedLanguage || null },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only measure the main editor tab load (not background translation tabs)
    if (mainPageTab === 0 && !isLoading && !projectContextLoading && effectiveProjectId) {
      endSpan('ui_icf_editor_load', { status: error ? 'error' : 'ok' });
    }
  }, [mainPageTab, isLoading, projectContextLoading, effectiveProjectId, error]);
  const [countryEntries, setCountryEntries] = useState([]);
  const [countryEntriesLoading, setCountryEntriesLoading] = useState(false);
  const [countryEntriesError, setCountryEntriesError] = useState(null);
  const [isCountryDialogOpen, setIsCountryDialogOpen] = useState(false);
  const [newCountryName, setNewCountryName] = useState('');
  const [countrySelectionOptions, setCountrySelectionOptions] = useState([]);
  const [countryNameMode, setCountryNameMode] = useState('selected'); // selected | custom
  const [countryNameError, setCountryNameError] = useState('');
  const [countryCloneSource, setCountryCloneSource] = useState('base');
  const [siteEntries, setSiteEntries] = useState([]);
  const [siteEntriesLoading, setSiteEntriesLoading] = useState(false);
  const [siteEntriesError, setSiteEntriesError] = useState(null);
  const [isSiteDialogOpen, setIsSiteDialogOpen] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [siteCountryForDialog, setSiteCountryForDialog] = useState('');
  const [siteNameError, setSiteNameError] = useState('');
  // State for Languages table
  const [languages, setLanguages] = useState([]); // Array of language entries from backend
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState(null);
  const [isApprovedTranslation, setIsApprovedTranslation] = useState(false); // Track if current translation is approved
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  // Step-by-step selection state
  const [selectedCountryForLanguage, setSelectedCountryForLanguage] = useState('');
  const [selectedSiteForLanguage, setSelectedSiteForLanguage] = useState('');
  const [availableSitesForLanguage, setAvailableSitesForLanguage] = useState([]);
  const [newLanguage, setNewLanguage] = useState('');
  const [customLanguageText, setCustomLanguageText] = useState('');
  const [selectedVersionForLanguage, setSelectedVersionForLanguage] = useState('');
  const [availableVersionsForLanguage, setAvailableVersionsForLanguage] = useState([]);
  const [languageError, setLanguageError] = useState('');
  const [needsInitialGeneration, setNeedsInitialGeneration] = useState(false);
  const [isGeneratingAllSections, setIsGeneratingAllSections] = useState(false);
  const [isValidatingICF, setIsValidatingICF] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [translateState, setTranslateState] = useState({
    open: false,
    language: SUPPORTED_TRANSLATION_LANGUAGES[0],
    customLanguage: '',
    translation: '',
    loading: false,
    sectionId: null,
    sectionTitle: '',
    error: null,
  });

  // State for Add Section dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [customSectionTitle, setCustomSectionTitle] = useState('');
  const [editorTab, setEditorTab] = useState(0); // 0 Sections, 1 Title page
  const [addDialogTab, setAddDialogTab] = useState(0); // 0 Template, 1 Custom
  const [userEditPromptBySection, setUserEditPromptBySection] = useState({});

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      console.log('ICFEditor: Loading project from URL:', projectIdFromUrl);
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  useEffect(() => {
    if (selectedCountry || selectedSite) {
      setMainPageTab(0);
    }
  }, [selectedCountry, selectedSite]);

  // Apply clinical background to body while on ICF page
  useEffect(() => {
    document.body.classList.add('icf-clinical-bg');
    return () => document.body.classList.remove('icf-clinical-bg');
  }, []);

  // Keyboard shortcut to toggle preview mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        setMode(mode === 'edit' ? 'preview' : 'edit');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode]);

  const refreshCountryEntries = useCallback(async () => {
    if (!effectiveProjectId) return;
    try {
      setCountryEntriesLoading(true);
      setCountryEntriesError(null);
      const entries = await documentEditorService.getICFCountrySummaries(effectiveProjectId);
      setCountryEntries(Array.isArray(entries) ? entries : []);
    } catch (err) {
      console.error('Error fetching country-specific ICF summaries:', err);
      setCountryEntriesError('Failed to load country-specific ICF summaries.');
    } finally {
      setCountryEntriesLoading(false);
    }
  }, [effectiveProjectId]);

  const refreshSiteEntries = useCallback(
    async (options = {}) => {
      if (!effectiveProjectId) return;
      try {
        setSiteEntriesLoading(true);
        setSiteEntriesError(null);
        const entries = await documentEditorService.getICFSiteSummaries(
          effectiveProjectId,
          options
        );
        setSiteEntries(Array.isArray(entries) ? entries : []);
      } catch (err) {
        console.error('Error fetching site-specific ICF summaries:', err);
        setSiteEntriesError('Failed to load site-specific ICF summaries.');
      } finally {
        setSiteEntriesLoading(false);
      }
    },
    [effectiveProjectId]
  );

  const refreshLanguages = useCallback(async () => {
    if (!effectiveProjectId) return;
    try {
      setLanguagesLoading(true);
      setLanguagesError(null);
      const langEntries = await documentEditorService.getICFLanguages(effectiveProjectId);
      setLanguages(Array.isArray(langEntries) ? langEntries : []);
    } catch (err) {
      console.error('Error fetching ICF languages:', err);
      setLanguagesError('Failed to load languages.');
    } finally {
      setLanguagesLoading(false);
    }
  }, [effectiveProjectId]);

  // Also refresh languages when viewing Main ICF tab with a language selected (to check approval status)
  useEffect(() => {
    if (mainPageTab === 0 && selectedLanguage && effectiveProjectId) {
      refreshLanguages();
    }
  }, [mainPageTab, selectedLanguage, effectiveProjectId, refreshLanguages]);

  // Fetch available section titles and sections on component mount, or when project changes
  useEffect(() => {
    if (!effectiveProjectId || projectContextLoading) {
      if (!effectiveProjectId && !projectContextLoading) {
        setError('Please select a project to view its documents.');
        setIsLoading(false);
      }
      return;
    }

      fetchSectionTitles();

    if (mainPageTab === 0) {
      fetchSections();
    } else {
      setIsLoading(false);
    }
  }, [
    effectiveProjectId,
    projectContextLoading,
    mainPageTab,
    selectedCountry,
    selectedSite,
    selectedLanguage,
    selectedVersion,
    location.search,
  ]); // Re-run when translation context in the URL changes

  useEffect(() => {
    if (!effectiveProjectId) return;
    if (mainPageTab === 1) {
      refreshCountryEntries();
      refreshSiteEntries();
      refreshLanguages();
    }
  }, [effectiveProjectId, mainPageTab, refreshCountryEntries, refreshSiteEntries, refreshLanguages]);

  // Clear accuracy data when country/site/version/language changes to prevent showing wrong accuracies
  useEffect(() => {
    console.log('🧹 Clearing accuracy data due to context change (country/site/version/language)');
    setRetranslatedSections({});
    setShowAccuracy({});
    setShowRetranslatedText({});
    setAccuracyMethod({});
    setIsRetranslating({});
    setHighlightedRetranslatedHtml({});
    setTranslatedSections([]);
  }, [selectedCountry, selectedSite, selectedVersion, selectedLanguage]);

  // Check if current language is approved
  useEffect(() => {
    // If opened from approved translation via URL parameter, mark as approved
    if (fromApprovedTranslation) {
      setIsApprovedTranslation(true);
      return;
    }
    
    if (selectedLanguage && selectedCountry && selectedSite && languages.length > 0) {
      const currentLangEntry = languages.find(
        lang => lang.language === selectedLanguage &&
        lang.country === selectedCountry &&
        lang.site === selectedSite &&
        (selectedVersion ? lang.version === selectedVersion : true)
      );
      setIsApprovedTranslation(currentLangEntry?.approved === true);
    } else {
      setIsApprovedTranslation(false);
    }
  }, [selectedLanguage, selectedCountry, selectedSite, selectedVersion, languages, fromApprovedTranslation]);

  // Fetch available section titles
  const fetchSectionTitles = async () => {
    try {
      if (!effectiveProjectId) return;
      const titles = await getPredefinedICFSections(effectiveProjectId); // Pass projectId
      setAvailableSectionTitles(titles);
      setSelectedSectionTitle(titles[0] || ''); // Set default to first available
    } catch (err) {
      console.error('Error fetching ICF section titles:', err);
      setError('Failed to load available ICF section titles.');
    }
  };

  // Fetch sections
  const fetchSections = async (overrideCountry, overrideSite) => {
    if (!effectiveProjectId) return;
    setIsLoading(true);
    setError(null);
    const countryContext =
      overrideCountry !== undefined ? overrideCountry : selectedCountry || null;
    const siteContext =
      overrideSite !== undefined ? overrideSite : selectedSite || null;
    const requestCountry = countryContext ? countryContext : '';
    const requestSite = siteContext ? siteContext : '';
    const isContextual = Boolean(countryContext) || Boolean(siteContext);
    try {
      // IMPORTANT: Fetch sections based on the selected context (country/site)
      // If country/site is selected, fetch from that specific context to get the correct English version
      // This ensures the left side (English) shows the correct version for the selected country/site
      let sectionsToFetch = { country: requestCountry, site: requestSite };
      
      if (selectedLanguage) {
        // When translation is enabled, we need to fetch from the CORRECT context
        // If a country/site is selected, fetch from that context to get the right English version
        // DO NOT fetch from main level if country/site is selected - it will show wrong English version
        console.log(`🌐 Language selected - fetching sections from ${isContextual ? 'country/site-specific' : 'MAIN'} ICF level`);
        console.log(`   Context: country="${countryContext || 'MAIN'}", site="${siteContext || 'MAIN'}"`);
        console.log(`   Version: ${selectedVersion || 'latest'} will be used for translation`);
      }
      
      console.log(`📡 Fetching sections with context: country="${sectionsToFetch.country || 'MAIN'}", site="${sectionsToFetch.site || 'MAIN'}"`);
      const fetchedSections = await documentEditorService.getAllICFSections(effectiveProjectId, sectionsToFetch);
      let normalizedSections = fetchedSections || [];
      
      console.log(`📥 Fetched ${normalizedSections.length} sections from backend (context: country="${sectionsToFetch.country || 'MAIN'}", site="${sectionsToFetch.site || 'MAIN'}")`);
      console.log(`📋 Section titles:`, normalizedSections.map(s => s.title || s._id));
      
      // Log first section to verify correct context
      if (normalizedSections.length > 0) {
        const firstSection = normalizedSections[0];
        console.log(`🔍 First section details:`, {
          title: firstSection.title,
          country: firstSection.country || sectionsToFetch.country || 'MAIN',
          site: firstSection.site || sectionsToFetch.site || 'MAIN',
          hasVersions: Array.isArray(firstSection.versions) && firstSection.versions.length > 0,
          versionCount: firstSection.versions?.length || 0,
          latestVersion: getLatestIcfVersionLabel(firstSection) || 'N/A',
        });
      }

      normalizedSections = sortIcfSectionsNaturally(normalizedSections);
      
      console.log(`📊 Fetched ${normalizedSections.length} sections from backend`);
      console.log(`📋 Section titles (sorted):`, normalizedSections.map(s => s.title || s._id));
      console.log(`📍 Fetch context: country="${sectionsToFetch.country}", site="${sectionsToFetch.site}"`);
      
      // If a specific version is selected (e.g., when viewing a language), extract that version's content
      if (selectedVersion && selectedVersion !== 'latest') {
        console.log(`📌 Version selected: ${selectedVersion} - extracting content from specific version`);
        
        // Extract version number from label (e.g., "v2_jupiter_hospital" -> 2)
        const versionNumberMatch = selectedVersion.match(/v(\d+)/i);
        const targetVersionNumber = versionNumberMatch ? parseInt(versionNumberMatch[1]) : null;
        
        // Check if this is a site-specific version (has site name in label)
        const isSiteSpecificVersion = selectedVersion.includes('_') && selectedVersion.match(/v\d+_/);
        
        normalizedSections = normalizedSections.map(section => {
          // Find the matching version in the section's versions array
          const versions = Array.isArray(section.versions) ? section.versions : [];
          
          // Try exact label match first
          let matchingVersion = versions.find(v => v.label === selectedVersion);
          
          // If not found and it's a site-specific version, try matching by globalVersionNumber
          // (site-specific versions share the same globalVersionNumber across all sections)
          if (!matchingVersion && targetVersionNumber) {
            matchingVersion = versions.find(v => 
              v.globalVersionNumber === targetVersionNumber || 
              (v.label && v.label.includes(`v${targetVersionNumber}`))
            );
          }
          
          // If still not found, try matching by versionNumber as last resort
          if (!matchingVersion && targetVersionNumber) {
            matchingVersion = versions.find(v => v.versionNumber === targetVersionNumber);
          }
          
          if (matchingVersion && matchingVersion.output) {
            console.log(`✓ Found version ${matchingVersion.label || selectedVersion} for section "${section.title}"`);
            // Create a copy of the section with the selected version's content
            return {
              ...section,
              output: matchingVersion.output, // Use the selected version's output
              context: matchingVersion.context || section.context,
              prompt: matchingVersion.prompt || section.prompt,
            };
          } else {
            // Log available versions for debugging
            const availableLabels = versions.map(v => v.label || `v${v.globalVersionNumber || v.versionNumber}`).join(', ');
            console.warn(`⚠ Version ${selectedVersion} not found for section "${section.title}"`);
            console.warn(`  Available versions: ${availableLabels || 'none'}`);
            console.warn(`  Using latest version instead`);
            return section; // Keep original if version not found
          }
        });
        console.log(`✅ Applied version ${selectedVersion} to ${normalizedSections.length} sections`);
      }
      
      setSections(normalizedSections);
      const regularSections = normalizedSections.filter((section) => !isICFTitlePageSection(section));
      setNeedsInitialGeneration(!isContextual && regularSections.length === 0);
      
      // If language is selected, translate all sections (with DB caching)
      if (selectedLanguage && normalizedSections.length > 0) {
        setIsTranslating(true);
        try {
          console.log(`Starting translation for ${normalizedSections.length} sections`);
          
          // Process sections in batches to avoid overwhelming the API
          // IMPORTANT: Use array with fixed indices to preserve original order
          const batchSize = 5; // Process 5 sections at a time
          const translated = new Array(normalizedSections.length); // Pre-allocate array to preserve order
          const totalBatches = Math.ceil(normalizedSections.length / batchSize);
          
          console.log(`🔄 Starting batch processing: ${normalizedSections.length} sections, ${totalBatches} batches, batchSize=${batchSize}`);
          
          for (let i = 0; i < normalizedSections.length; i += batchSize) {
            const batchNumber = Math.floor(i / batchSize) + 1;
            const batch = normalizedSections.slice(i, i + batchSize);
            console.log(`\n🔄 [Batch ${batchNumber}/${totalBatches}] Starting batch at index ${i}`);
            console.log(`[Batch ${batchNumber}] Processing sections ${i + 1}-${Math.min(i + batchSize, normalizedSections.length)} of ${normalizedSections.length}`);
            console.log(`[Batch ${batchNumber}] Batch sections:`, batch.map(s => s.title || s._id));
            
            // Verify we're still in the loop
            if (i >= normalizedSections.length) {
              console.error(`❌ Loop condition failed: i=${i}, length=${normalizedSections.length}`);
              break;
            }
            
            console.log(`[Batch ${batchNumber}] Loop check passed. Continuing...`);
            
            try {
              const batchResults = await Promise.allSettled(
                batch.map(async (section, batchIndex) => {
                  const originalIndex = i + batchIndex; // Original index in normalizedSections
                  const sectionNumber = originalIndex + 1;
                  try {
                    console.log(`[${sectionNumber}/${normalizedSections.length}] Checking section: ${section.title || section._id}`);
                    
                    // First, check if translation exists in database
                    try {
                      console.log(`[${sectionNumber}/${normalizedSections.length}] Checking DB for translation: lang=${selectedLanguage}, version=${selectedVersion || 'latest'}, country=${countryContext || 'null'}, site=${siteContext || 'null'}`);
                      const savedTranslations = await documentEditorService.getICFSectionTranslations(
                        section._id,
                        effectiveProjectId,
                        {
                          language: selectedLanguage,
                          version: selectedVersion || 'latest',
                          country: countryContext || null,
                          site: siteContext || null,
                        }
                      );
                      
                      // If we have a saved translation, use it
                      if (savedTranslations && savedTranslations.length > 0) {
                        const savedTranslation = savedTranslations[0]; // Get the most recent one
                        console.log(`[${sectionNumber}/${normalizedSections.length}] ✓ Using saved translation from DB: ${section.title || section._id} (version: ${savedTranslation.version || 'unknown'}, country: ${savedTranslation.country || 'null'}, site: ${savedTranslation.site || 'null'})`);
                        
                        // If accuracy data exists, load it (but don't show retranslated text by default)
                        if (savedTranslation.accuracy !== null && savedTranslation.accuracy !== undefined) {
                          // Load BOTH LLM and rule-based accuracies from database if they exist
                          const storedAccuracy = savedTranslation.accuracy;
                          const retranslatedText = savedTranslation.retranslatedText || '';
                          
                          // Load method-specific accuracies from the new database fields
                          const accuracyLLM = savedTranslation.accuracyLLM || null;
                          const accuracyRuleBased = savedTranslation.accuracyRuleBased || null;
                          
                          // Determine which method was last used based on explanation or default to 'llm'
                          const storedMethod = savedTranslation.accuracyExplanation?.includes('rule-based') ? 'rule-based' : 'llm';
                          
                          setRetranslatedSections(prev => ({
                            ...prev,
                            [section._id]: {
                              text: retranslatedText,
                              accuracy: storedAccuracy, // Use stored accuracy (latest one)
                              accuracyLLM: accuracyLLM, // Load LLM accuracy from DB
                              accuracyRuleBased: accuracyRuleBased, // Load rule-based accuracy from DB
                              explanation: savedTranslation.accuracyExplanation || '',
                              keyDifferences: savedTranslation.accuracyKeyDifferences || [],
                              method: storedMethod // Use the method that was last stored
                            }
                          }));
                          
                          // Set the accuracy method to match what was stored
                          setAccuracyMethod(prev => ({
                            ...prev,
                            [section._id]: storedMethod
                          }));
                          
                          // Show accuracy by default when loaded from DB, but keep language version visible
                          setShowAccuracy(prev => ({
                            ...prev,
                            [section._id]: true
                          }));
                          // Don't show retranslated text by default - keep language version visible
                          setShowRetranslatedText(prev => ({
                            ...prev,
                            [section._id]: false
                          }));
                          
                          console.log(`✅ Loaded accuracy from DB for section ${section._id}: ${storedAccuracy}% (method: ${storedMethod}, version: ${savedTranslation.version || 'unknown'}, country: ${savedTranslation.country || 'null'}, site: ${savedTranslation.site || 'null'})`);
                        }
                        
                        return {
                          originalIndex, // Preserve original index
                          section: {
                            ...section,
                            translatedOutput: savedTranslation.translation || section.output,
                          }
                        };
                      }
                    } catch (dbErr) {
                      console.warn(`[${sectionNumber}/${normalizedSections.length}] Error fetching saved translation:`, dbErr);
                      // Continue to translate if DB fetch fails
                    }
                    
                    // If no saved translation exists, fetch new translation from API
                    // The API will check DB and save if needed
                    console.log(`[${sectionNumber}/${normalizedSections.length}] Translating via API: ${section.title || section._id}...`);
                    const response = await documentEditorService.translateICFSection({
                      sectionId: section._id,
                      projectId: effectiveProjectId,
                      targetLanguage: selectedLanguage,
                      baseCountry: countryContext || null,
                      version: selectedVersion || 'latest',
                      country: countryContext || null,
                      site: siteContext || null,
                    });
                    
                    const translation = response?.translation || section.output;
                    console.log(`[${sectionNumber}/${normalizedSections.length}] ✓ Translated: ${section.title || section._id}`);
                    
                    return {
                      originalIndex, // Preserve original index
                      section: {
                        ...section,
                        translatedOutput: translation,
                      }
                    };
                  } catch (err) {
                    const failureMessage = err?.message || err?.response?.data?.message || err?.data?.message || 'Translation failed';
                    console.error(`[${sectionNumber}/${normalizedSections.length}] ✗ Error translating section ${section._id} (${section.title}):`, err);
                    return {
                      originalIndex, // Preserve original index
                      section: {
                        ...section,
                        translatedOutput: section.output, // Fallback to original (untranslated) content
                        translationFailed: true,
                        translationError: failureMessage,
                      }
                    };
                  }
                })
              );

              // Process results, handling both fulfilled and rejected promises
              // Place each result at its original index to preserve order
              batchResults.forEach((result, batchIndex) => {
                const originalIndex = i + batchIndex;
                if (result.status === 'fulfilled') {
                  const { originalIndex: idx, section } = result.value;
                  translated[idx] = section; // Place at original index
                } else {
                  // If promise was rejected, use original section
                  const sectionNumber = originalIndex + 1;
                  console.error(`[${sectionNumber}/${normalizedSections.length}] ✗ Promise rejected for section in batch:`, result.reason);
                  translated[originalIndex] = {
                    ...batch[batchIndex],
                    translatedOutput: batch[batchIndex].output,
                    translationFailed: true,
                    translationError: result.reason?.message || result.reason?.response?.data?.message || 'Translation failed',
                  };
                }
              });
              
              const completedCount = translated.filter(s => s !== undefined).length;
              console.log(`✅ [Batch ${batchNumber}/${totalBatches}] Completed successfully. Total translated so far: ${completedCount}/${normalizedSections.length}`);
              
              // Small delay between batches to avoid rate limiting
              if (i + batchSize < normalizedSections.length) {
                console.log(`⏳ Waiting 500ms before next batch... (next batch will start at index ${i + batchSize})`);
                await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                console.log(`✅ Delay complete. Continuing to next batch...`);
              } else {
                console.log(`🏁 [Batch ${batchNumber}/${totalBatches}] This was the last batch`);
              }
            } catch (batchErr) {
              console.error(`❌ [Batch ${batchNumber}/${totalBatches}] Error processing batch:`, batchErr);
              console.error(`Batch error details:`, batchErr.stack || batchErr.message);
              // Add all sections from this batch with original output as fallback at correct indices
              batch.forEach((section, batchIndex) => {
                const originalIndex = i + batchIndex;
                translated[originalIndex] = {
                  ...section,
                  translatedOutput: section.output,
                  translationFailed: true,
                  translationError: batchErr?.message || 'Translation failed',
                };
              });
              // Continue to next batch even if this one failed
              console.log(`⚠️ [Batch ${batchNumber}] Error handled, continuing to next batch...`);
            }
            
            // Log progress after each batch
            const progressCount = translated.filter(s => s !== undefined).length;
            console.log(`📊 [Progress] After batch ${batchNumber}: ${progressCount}/${normalizedSections.length} sections processed`);
            console.log(`🔄 Loop will continue: i=${i}, i + batchSize=${i + batchSize}, normalizedSections.length=${normalizedSections.length}, condition=${i + batchSize < normalizedSections.length}`);
          }
          
          console.log(`\n✅ Loop finished. Final check: processed ${translated.filter(s => s !== undefined).length} sections`);
          
          console.log(`[Loop Complete] Finished processing all batches. Loop variable i would be: ${normalizedSections.length}`);
          
          // Filter out any undefined entries (shouldn't happen, but just in case)
          let finalTranslated = translated.filter(s => s !== undefined);
          
          // Ensure translated sections are in the same order as normalizedSections
          // Map by section ID to original order
          const sectionOrderMap = new Map();
          normalizedSections.forEach((section, index) => {
            sectionOrderMap.set(section._id, index);
          });
          
          // Sort translated sections to match original order
          finalTranslated = finalTranslated.sort((a, b) => {
            const aIndex = sectionOrderMap.get(a._id) ?? 999;
            const bIndex = sectionOrderMap.get(b._id) ?? 999;
            return aIndex - bIndex;
          });
          
          console.log(`✅ Translation complete! Processed ${finalTranslated.length} out of ${normalizedSections.length} sections`);
          console.log(`📋 Translated section titles (in order):`, finalTranslated.map(s => s.title || s._id));
          
          // Verify all sections were processed
          if (finalTranslated.length !== normalizedSections.length) {
            console.warn(`⚠️ WARNING: Only ${finalTranslated.length} sections translated out of ${normalizedSections.length} total sections!`);
            const missingSections = normalizedSections.filter(s => 
              !finalTranslated.find(t => t._id === s._id)
            );
            console.warn(`⚠️ Missing sections:`, missingSections.map(s => s.title || s._id));
          }
          
          setTranslatedSections(finalTranslated);
        } catch (err) {
          console.error('❌ Fatal error during translation:', err);
          setTranslatedSections([]);
        } finally {
          setIsTranslating(false);
        }
      } else {
        setTranslatedSections([]);
      }
    } catch (err) {
      setError('Failed to load ICF sections.');
      console.error('Error fetching ICF sections:', err);
      if (!isContextual) {
        setNeedsInitialGeneration(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createTitlePageSection = async (existingSections = []) => {
    const titlePageDef = getICFTitlePageDefinition();
    const alreadyExists = existingSections.find(
      (section) =>
        isICFTitlePageSection(section) &&
        (section.project?.toString?.() === effectiveProjectId || section.project === effectiveProjectId)
    );

    if (alreadyExists) {
      return alreadyExists;
    }

    let protocolContext = '';
    try {
      protocolContext = await fetchICFTitlePageContext(effectiveProjectId);
    } catch (err) {
      console.warn('Error fetching title page context:', err);
    }

    const fullContext = protocolContext || titlePageDef.context || '';
    const generatedContent = await generateIcfContentViaJob(
      titlePageDef.prompt,
      fullContext,
      '',
      titlePageDef.title,
      effectiveProjectId
    );

    const createdSection = await documentEditorService.createICFSection(
      {
        title: titlePageDef.title,
        context: fullContext,
        prompt: titlePageDef.prompt,
        output: generatedContent,
        projectId: effectiveProjectId,
        country: selectedCountry || '',
        site: selectedSite || '',
      },
      effectiveProjectId
    );

    if (createdSection && createdSection._id) {
      await documentEditorService.addNewICFVersion(
        {
          sectionId: createdSection._id,
          title: titlePageDef.title,
          context: fullContext,
          prompt: titlePageDef.prompt,
          output: generatedContent,
          projectId: effectiveProjectId,
        },
        effectiveProjectId
      );
    }

    return createdSection;
  };

  const generateTitlePageSection = async () => {
    if (!effectiveProjectId) return;

    setIsGenerating((prev) => ({ ...prev, titlePage: true }));
    setError(null);

    try {
      await createTitlePageSection(sections);
      await fetchSections();
      setEditorTab(1);
    } catch (err) {
      console.error('Error generating ICF title page:', err);
      setError('Failed to generate ICF title page. Please try again.');
    } finally {
      setIsGenerating((prev) => ({ ...prev, titlePage: false }));
    }
  };

  const runICFSelfValidationAndApply = async ({ silent = false, skipEmptyCheck = false } = {}) => {
    if (!effectiveProjectId) return;
    if (selectedCountry) {
      if (!silent) {
        alert('ICF self-validation is only available for the main ICF.');
      }
      return;
    }

    if (!skipEmptyCheck) {
      const sectionsWithContent = (sections || []).filter((s) => String(s.output || '').trim());
      if (sectionsWithContent.length === 0) {
        if (!silent) {
          alert('Generate ICF sections before running validation.');
        }
        return;
      }
    }

    setIsValidatingICF(true);
    setError(null);
    startSpan('ui_icf_self_validation', { page: 'icf_editor', meta: { projectId: effectiveProjectId } });
    try {
      const result = await documentEditorService.validateAndCorrectICF(effectiveProjectId, {
        country: '',
        site: '',
      });
      const updatedSections = result?.sections
        || await documentEditorService.getAllICFSections(effectiveProjectId, { country: '', site: '' });
      const normalizedSections = updatedSections || [];
      setSections(normalizedSections);
      const regularSections = normalizedSections.filter((section) => !isICFTitlePageSection(section));
      setNeedsInitialGeneration(regularSections.length === 0);
      if (!silent) {
        const updated = result?.sectionsUpdated ?? 0;
        const diag = result?.diagnostics;
        let msg = result?.message || 'ICF validation complete.';
        if (updated === 0 && diag?.unmatched?.length > 0) {
          msg += `\n\nUnmatched sections: ${diag.unmatched.map((u) => u.title).join(', ')}`;
        }
        alert(msg);
      }
      endSpan('ui_icf_self_validation', { status: 'ok', meta: { sectionsUpdated: result?.sectionsUpdated ?? 0 } });
    } catch (err) {
      console.error('ICF self-validation failed:', err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        (typeof err === 'string' ? err : null) ||
        'Failed to validate and optimize ICF.';
      setError(msg);
      if (!silent) alert(msg);
      endSpan('ui_icf_self_validation', { status: 'error' });
    } finally {
      setIsValidatingICF(false);
    }
  };

  const generateAllICFSections = async () => {
    if (!effectiveProjectId) return;
    if (selectedCountry) {
      alert('Generate ICF is only available for the main ICF.');
      return;
    }

    setIsGeneratingAllSections(true);
    setIsLoading(true);
    setError(null);

    try {
      const predefinedSections = await getPredefinedICFSections(effectiveProjectId); // Pass projectId
      let existingSections = await documentEditorService.getAllICFSections(effectiveProjectId, {
        country: '',
        site: '',
      }); // Pass projectId
      existingSections = existingSections || [];

        for (const section of predefinedSections) {
          try {
          const alreadyExists = existingSections.find(
            (s) =>
              s.title === section.title &&
              !isICFTitlePageSection(s) &&
              (s.project?.toString?.() === effectiveProjectId || s.project === effectiveProjectId)
          );

          if (alreadyExists) {
              continue;
            }

            let protocolContext = '';
            try {
              const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
              if (section.protocolSources) {
              protocolContext = await fetchProtocolContextForICF(
                effectiveProjectId,
                section.protocolSources
              );
              }
            } catch (err) {
            console.warn('Error fetching protocol context during generation:', err);
            }
            
            const sectionContext = section.context || '';
            const fullContext = protocolContext ? `${sectionContext}\n${protocolContext}` : sectionContext;
            
            const generatedContent = await generateIcfContentViaJob(
              section.prompt || '',
              fullContext,
              '',
              section.title,
              effectiveProjectId
            );

          const createdSection = await documentEditorService.createICFSection(
            {
              title: section.title,
              context: sectionContext,
              prompt: section.prompt || `Default prompt for ${section.title}`,
              output: generatedContent,
              projectId: effectiveProjectId,
              country: '',
              site: '',
            },
            effectiveProjectId
          );

            if (createdSection && createdSection._id) {
            await documentEditorService.addNewICFVersion(
              {
                sectionId: createdSection._id,
                title: section.title,
                context: sectionContext,
                prompt: section.prompt || `Default prompt for ${section.title}`,
                output: generatedContent,
                projectId: effectiveProjectId,
              },
              effectiveProjectId
            );
            }
          } catch (createErr) {
          console.error('Failed to create or version ICF section:', createErr);
        }
      }

      try {
        await createTitlePageSection(existingSections);
      } catch (titlePageErr) {
        console.error('Failed to create ICF title page:', titlePageErr);
      }

      const updatedSections = await documentEditorService.getAllICFSections(effectiveProjectId, {
        country: '',
        site: '',
      }); // Pass projectId
      const normalizedSections = updatedSections || [];
      setSections(normalizedSections);
      const regularSections = normalizedSections.filter((section) => !isICFTitlePageSection(section));
      setNeedsInitialGeneration(regularSections.length === 0);

      if (regularSections.length > 0) {
        try {
          await runICFSelfValidationAndApply({ silent: true, skipEmptyCheck: true });
        } catch (validationErr) {
          console.warn('Post-generation ICF self-validation failed:', validationErr);
        }
      }
    } catch (err) {
      console.error('Error generating ICF sections:', err);
      setError('Failed to generate ICF sections. Please try again.');
    } finally {
      setIsGeneratingAllSections(false);
      setIsLoading(false);
    }
  };

  // Helper functions for HTML text editing
  const extractTextContent = (htmlContent) => {
    if (!htmlContent) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

const convertPlainTextToHtml = (text = '') => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const paragraphs = trimmed.split(/\r?\n+/).map((line) => line.trim());
  return paragraphs.map((line) => `<p>${line || '&nbsp;'}</p>`).join('');
  };

  const handleInputChange = async (_id, field, value) => {
    try {
      if (!effectiveProjectId) return;
      const updatedSection = await documentEditorService.updateICFSection(
        _id,
        { [field]: value },
        effectiveProjectId
      ); // Pass projectId
      if (updatedSection) {
        setSections((prev) =>
          prev.map((section) => (section._id === _id ? updatedSection : section))
        );
        if (field === 'output') {
          setEditedTextContent((prev) => {
            const copy = { ...prev };
            delete copy[_id];
            return copy;
          });
        }
      }
      if (selectedCountry) {
        await refreshCountryEntries();
      }
      if (selectedSite) {
        await refreshSiteEntries();
      }
    } catch (error) {
      console.error('Error updating ICF section:', error);
    }
  };

  // Handle text content changes while preserving HTML structure
  const handleTextContentChange = async (_id, field, newText) => {
    // Update local state immediately for responsive UI
    setEditedTextContent(prev => ({
      ...prev,
      [_id]: newText
    }));
    
    // Defer persistence until user explicitly saves the section
    setEditedTextContent((prev) => ({
      ...prev,
      [_id]: newText,
    }));
  };

  const handleSaveSectionContent = async (_id) => {
    const pendingText = editedTextContent[_id];
    const html = convertPlainTextToHtml(pendingText || '');
    await handleInputChange(_id, 'output', html);
  };

  const getUserEditPrompt = (sectionId) => userEditPromptBySection[sectionId] || '';

  const setUserEditPrompt = (sectionId, value) => {
    setUserEditPromptBySection((prev) => ({ ...prev, [sectionId]: value }));
  };

  const runTitlePageGeneration = async (sectionId, updateInstructions = '', { requireUpdateInstructions = false } = {}) => {
    const trimmedUpdate = (updateInstructions || '').trim();
    if (requireUpdateInstructions && !trimmedUpdate) {
      setError('Enter editing instructions before generating.');
      return;
    }

    const section = sections.find((s) => s._id === sectionId);
    if (!section || !isICFTitlePageSection(section)) {
      setError('Title page section not found.');
      return;
    }

    setIsGenerating((prev) => ({ ...prev, [sectionId]: true }));
    setError(null);

    try {
      let protocolContext = '';
      try {
        protocolContext = await fetchICFTitlePageContext(effectiveProjectId);
      } catch (err) {
        console.warn('Error fetching title page context:', err);
        protocolContext = section.context || '';
      }

      const versionsResp = await documentEditorService.getAllICFVersions(sectionId, effectiveProjectId);
      const priorVersions = versionsResp?.versions || versionsResp?.data?.versions || [];

      const context = buildIcfSectionGenerateContext({
        section,
        priorVersions,
        predefinedPrompt: getTitlePageBasePrompt(),
        protocolContext,
      });

      const generationPrompt = buildIcfUserEditGenerationPrompt(trimmedUpdate, section.title);

      const generatedOutput = await generateIcfContentViaJob(
        generationPrompt,
        context,
        section.output || '',
        section.title,
        effectiveProjectId
      );

      if (protocolContext && protocolContext !== section.context) {
        await documentEditorService.updateICFSection(
          sectionId,
          { context: protocolContext },
          effectiveProjectId
        );
      }

      const updatedSection = await documentEditorService.updateICFSection(
        sectionId,
        {
          output: generatedOutput,
          prompt: trimmedUpdate,
          provenance: 'aidrafted',
        },
        effectiveProjectId
      );

      if (updatedSection) {
        setSections((prev) =>
          prev.map((s) => (s._id === sectionId ? { ...updatedSection, prompt: '' } : s))
        );
      }

      setUserEditPromptBySection((prev) => {
        const copy = { ...prev };
        delete copy[sectionId];
        return copy;
      });
      setEditedTextContent((prev) => {
        const updated = { ...prev };
        delete updated[sectionId];
        return updated;
      });
    } catch (err) {
      console.error('Error updating ICF title page:', err);
      setError(`Failed to update title page: ${err.message}`);
    } finally {
      setIsGenerating((prev) => ({ ...prev, [sectionId]: false }));
    }
  };

  const handleGenerateContent = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;

    if (isICFTitlePageSection(section)) {
      await runTitlePageGeneration(_id, getUserEditPrompt(_id), { requireUpdateInstructions: true });
      return;
    }

    const userPrompt = getUserEditPrompt(_id).trim();
    if (!userPrompt) {
      setError('Enter editing instructions before generating.');
      return;
    }

    setIsGenerating((prev) => ({ ...prev, [_id]: true }));
    setError(null);

    try {
      const versionsResp = await documentEditorService.getAllICFVersions(_id, effectiveProjectId);
      const priorVersions = versionsResp?.versions || versionsResp?.data?.versions || [];

      let protocolContext = '';
      let predefinedPrompt = '';
      try {
        const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
        const predefinedSections = await getPredefinedICFSections(effectiveProjectId);
        const predefinedSection = predefinedSections.find((s) => s.title === section.title);
        if (predefinedSection) {
          predefinedPrompt = predefinedSection.prompt || '';
          if (predefinedSection.protocolSources) {
            protocolContext = await fetchProtocolContextForICF(
              effectiveProjectId,
              predefinedSection.protocolSources
            );
          }
        }
      } catch (err) {
        console.warn('Error fetching ICF generation context:', err);
      }

      const context = buildIcfSectionGenerateContext({
        section,
        priorVersions,
        predefinedPrompt,
        protocolContext,
      });

      const generationPrompt = buildIcfUserEditGenerationPrompt(userPrompt, section.title);

      const generatedOutput = await generateIcfContentViaJob(
        generationPrompt,
        context,
        section.output || '',
        section.title,
        effectiveProjectId
      );

      const updatedSection = await documentEditorService.updateICFSection(
        _id,
        {
          output: generatedOutput,
          prompt: userPrompt,
          provenance: 'aidrafted',
        },
        effectiveProjectId
      );

      if (updatedSection) {
        setSections((prev) =>
          prev.map((s) => (s._id === _id ? { ...updatedSection, prompt: '' } : s))
        );
      }

      setUserEditPromptBySection((prev) => {
        const copy = { ...prev };
        delete copy[_id];
        return copy;
      });
      setEditedTextContent((prev) => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    } catch (err) {
      console.error('Error generating ICF content:', err);
      setError(`Failed to generate content: ${err.message}`);
    } finally {
      setIsGenerating((prev) => ({ ...prev, [_id]: false }));
    }
  };

  const handleReset = async () => {
    if (!effectiveProjectId) return;
    const confirmationMessage = selectedSite
      ? `Are you sure you want to reset all ICF sections for ${selectedSite}${selectedCountry ? ` (${selectedCountry})` : ''}? This action cannot be undone.`
      : selectedCountry
        ? `Are you sure you want to reset all ICF sections for ${selectedCountry}? This action cannot be undone.`
        : 'Are you sure you want to reset all ICF sections for this project? This action cannot be undone.';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

      try {
        setIsLoading(true);
      await documentEditorService.deleteAllICFSections(effectiveProjectId, {
        country: selectedCountry || undefined,
        site: selectedSite || undefined,
      });
        setSections([]);
      if (!selectedCountry) {
        setNeedsInitialGeneration(true);
      }
        setError(null);
      await fetchSections(selectedCountry, selectedSite);
      if (selectedCountry) {
        await refreshCountryEntries();
      }
      if (selectedSite) {
        await refreshSiteEntries();
      }
      } catch (error) {
        console.error('Error resetting ICF sections:', error);
        setError(`Failed to reset ICF sections: ${error.message}`);
      } finally {
        setIsLoading(false);
    }
  };

  const handleDeleteSection = async (_id) => {
    if (window.confirm('Are you sure you want to delete this ICF section? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await documentEditorService.deleteICFSection(_id, effectiveProjectId); // Pass projectId
        setSections((prev) => {
          const updated = prev.filter((section) => section._id !== _id);
          if (updated.length === 0 && !selectedCountry) {
            setNeedsInitialGeneration(true);
          }
          return updated;
        });
        setError(null);
        if (selectedCountry) {
          await refreshCountryEntries();
        }
        if (selectedSite) {
          await refreshSiteEntries();
        }
      } catch (error) {
        console.error('Error deleting ICF section:', error);
        setError(`Failed to delete ICF section: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMarkAsFinal = async (_id) => {
    try {
      // Update backend
      await documentEditorService.updateICFSection(_id, { final: true }, effectiveProjectId);
      // Update UI
      setSections(prevSections =>
        prevSections.map(section =>
          section._id === _id ? { ...section, final: true } : section
        )
      );
    } catch (err) {
      console.error("Failed to mark ICF section as final:", err);
      alert('Failed to mark ICF section as final.');
    }
  };

  const handleStyleAutofixApplied = async (sectionId, html) => {
    await documentEditorService.updateICFSection(sectionId, { output: html }, effectiveProjectId);
    setSections((prev) => prev.map((s) => (s._id === sectionId ? { ...s, output: html } : s)));
  };

  const handleExport = async (formatType) => {
    const orderedSections = titlePageSection
      ? [titlePageSection, ...icfContentSections]
      : icfContentSections;

    // If viewing approved translation, export translated sections instead
    let sectionsToExport = orderedSections;
    if (isApprovedTranslation && selectedLanguage && translatedSections.length > 0) {
      // Create sections with translated output for export
      sectionsToExport = orderedSections.map(section => {
        const translatedSection = translatedSections.find(ts => ts._id === section._id);
        return {
          ...section,
          output: translatedSection?.translatedOutput || section.output,
          title: section.title
        };
      });
    }
    
    if (formatType === 'word') {
      exportToWord(sectionsToExport);
    } else if (formatType === 'pdf') {
      exportToPdf(sectionsToExport);
    } else if (formatType === 'xml') {
      exportToXML(sectionsToExport);
    }
  };

  /**
   * Normalize titles for comparison: drop leading section numbers, collapse whitespace.
   * e.g. "1.2 Purpose of the Study" → "purpose of the study"
   */
  const normalizeIcfTitleForCompare = (text) =>
    String(text || '')
      .replace(/\u00A0/g, ' ')
      .replace(/^\s*\d+(?:\.\d+)*\.?\s*/u, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  /** Character bigrams of a string, for fuzzy similarity comparison. */
  const stringBigrams = (s) => {
    const grams = [];
    for (let i = 0; i < s.length - 1; i++) grams.push(s.slice(i, i + 2));
    return grams;
  };

  /**
   * Sørensen–Dice coefficient over character bigrams: robust fuzzy string
   * similarity (0-1) that tolerates reworded/reordered phrasing without being
   * fooled by a few shared filler words the way plain word-overlap would be
   * (e.g. "why", "this", "study", "being" alone aren't enough to call two
   * unrelated Q&A headings the same).
   */
  const diceCoefficient = (a, b) => {
    const ga = stringBigrams(a);
    const gb = stringBigrams(b);
    if (!ga.length || !gb.length) return 0;
    const counts = new Map();
    gb.forEach((g) => counts.set(g, (counts.get(g) || 0) + 1));
    let matches = 0;
    ga.forEach((g) => {
      const c = counts.get(g) || 0;
      if (c > 0) {
        matches++;
        counts.set(g, c - 1);
      }
    });
    return (2 * matches) / (ga.length + gb.length);
  };

  /**
   * True when two titles refer to the same section heading, even if reworded or
   * numbered differently (e.g. "2. About this study and Why You Are Being
   * Invited to Participate" vs "2.0 About This Study and Why You Are Invited"
   * — same heading, restated by content generation with slightly different
   * wording/number format).
   */
  const icfTitlesMatch = (a, b) => {
    const na = normalizeIcfTitleForCompare(a);
    const nb = normalizeIcfTitleForCompare(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    // Also allow exact raw match (including numbers)
    const ra = String(a || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const rb = String(b || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (ra === rb) return true;

    // Fuzzy fallback for restated headings. Skip very short titles, where
    // bigram similarity is unstable and could false-match unrelated headings.
    if (na.length < 8 || nb.length < 8) return false;
    return diceCoefficient(na, nb) >= 0.65;
  };

  /**
   * Remove a leading block that duplicates the section title so Word export
   * only keeps the single ICFHeading we add above the body.
   */
  const stripLeadingDuplicateSectionTitle = (html, sectionTitle) => {
    if (!html || !sectionTitle) return html || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const isTitleLikeNode = (el) => {
      if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
      const tag = String(el.tagName || '').toLowerCase();
      if (/^h[1-6]$/.test(tag)) return true;
      if (tag === 'p' || tag === 'div' || tag === 'span') {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) return false;
        const onlyStrong =
          el.children.length === 1 &&
          /^(STRONG|B)$/i.test(el.children[0].tagName) &&
          el.children[0].textContent?.replace(/\s+/g, ' ').trim() === text;
        // Short block whose full text is the title (not a long body paragraph)
        return onlyStrong || (text.length < 200 && icfTitlesMatch(text, sectionTitle));
      }
      return false;
    };

    const removeEmptyLeading = (parent) => {
      let node = parent.firstChild;
      while (
        node &&
        ((node.nodeType === Node.TEXT_NODE && !String(node.textContent || '').trim()) ||
          (node.nodeType === Node.ELEMENT_NODE &&
            !String(node.textContent || '').trim() &&
            !/^(HR|BR|IMG|TABLE)$/i.test(node.tagName)))
      ) {
        const next = node.nextSibling;
        node.remove();
        node = next;
      }
    };

    /** Walk into wrappers to find and remove the first title duplicate. */
    const tryStrip = (parent) => {
      removeEmptyLeading(parent);
      const first = parent.firstElementChild;
      if (!first) {
        const firstTextNode = Array.from(parent.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && String(n.textContent || '').trim()
        );
        if (firstTextNode && icfTitlesMatch(firstTextNode.textContent, sectionTitle)) {
          firstTextNode.remove();
          return true;
        }
        return false;
      }

      if (isTitleLikeNode(first) && icfTitlesMatch(first.textContent, sectionTitle)) {
        first.remove();
        return true;
      }

      // Descend into a single layout wrapper (div/section/article) that may wrap the title
      const tag = String(first.tagName || '').toLowerCase();
      if (
        (tag === 'div' || tag === 'section' || tag === 'article') &&
        first.children.length > 0
      ) {
        if (tryStrip(first)) return true;
      }

      return false;
    };

    tryStrip(tempDiv);
    return tempDiv.innerHTML;
  };

  // Convert basic HTML to docx children (headings, paragraphs, lists, tables, bold/italic)
  // ICF sections use Q&A: question text is typically <h2>/<h3> (or <strong>) within the section.
  // Those must be exported as bold subheadings — do NOT drop h1–h3 (only the section title
  // is stripped before this runs).
  const htmlToDocxChildren = (html, sectionTitle = '') => {
    if (!html) return [new DocxParagraph('')];
    const container = document.createElement('div');
    container.innerHTML = html;

    const children = [];
    let skippedLeadingTitle = false;
    const BLOCK_TAGS = new Set([
      'p', 'div', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'blockquote', 'pre', 'hr',
    ]);

    const normalizeText = (t) => (t || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();

    const hasBlockChild = (el) =>
      Array.from(el.childNodes || []).some(
        (cn) =>
          cn.nodeType === Node.ELEMENT_NODE &&
          BLOCK_TAGS.has(String(cn.tagName || '').toLowerCase())
      );

    const buildRuns = (n) => {
      const runs = [];
      const pushInline = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const t = normalizeText(node.textContent || '');
          if (t) runs.push(new TextRun(t));
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const ct = node.tagName.toLowerCase();
        if (ct === 'strong' || ct === 'b') {
          const t = normalizeText(node.textContent || '');
          if (t) runs.push(new TextRun({ text: t, bold: true }));
        } else if (ct === 'em' || ct === 'i') {
          const t = normalizeText(node.textContent || '');
          if (t) runs.push(new TextRun({ text: t, italics: true }));
        } else if (ct === 'u') {
          const t = normalizeText(node.textContent || '');
          if (t) runs.push(new TextRun({ text: t, underline: {} }));
        } else if (ct === 'br') {
          runs.push(new TextRun({ break: 1 }));
        } else if (ct === 'span' || ct === 'a' || ct === 'label') {
          node.childNodes.forEach(pushInline);
        } else if (!BLOCK_TAGS.has(ct)) {
          const t = normalizeText(node.textContent || '');
          if (t) runs.push(new TextRun(t));
        }
      };
      n.childNodes.forEach(pushInline);
      return runs;
    };

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = normalizeText(node.textContent || '');
        if (text) {
          // Skip a bare leading text node that duplicates the section title
          if (
            !skippedLeadingTitle &&
            sectionTitle &&
            children.length === 0 &&
            icfTitlesMatch(text, sectionTitle)
          ) {
            skippedLeadingTitle = true;
            return;
          }
          children.push(
            new DocxParagraph({
              text,
              style: 'ICFBody',
            })
          );
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const el = node;
      const tag = el.tagName?.toLowerCase();

      // Question headings and other in-section headings (ICF Q&A uses h2/h3 for each question).
      // h1/h2 are the primary Question Heading tier; h3+ are the "Optional Subheading" tier
      // the ICF editorial standard describes, so they get a visually subordinate size.
      if (/^h[1-6]$/.test(tag)) {
        const headingText = normalizeText(el.textContent || '');
        if (headingText) {
          // Skip first heading if it duplicates the section title we already emitted
          if (
            !skippedLeadingTitle &&
            sectionTitle &&
            children.length === 0 &&
            icfTitlesMatch(headingText, sectionTitle)
          ) {
            skippedLeadingTitle = true;
            return;
          }
          const level = parseInt(tag[1], 10);
          children.push(
            new DocxParagraph({
              text: headingText,
              style: level <= 2 ? 'ICFSubheading' : 'ICFQuestionSubheading',
            })
          );
        }
        return;
      }

      if (tag === 'p') {
        const runs = buildRuns(el);
        const plain = normalizeText(el.textContent || '');
        if (
          !skippedLeadingTitle &&
          sectionTitle &&
          children.length === 0 &&
          plain &&
          icfTitlesMatch(plain, sectionTitle)
        ) {
          skippedLeadingTitle = true;
          return;
        }
        if (runs.length) {
          // A paragraph immediately before a <table> that already reads like a table caption
          // (e.g. "Table 1: Visit Schedule") gets restyled/title-cased instead of left as body text.
          const nextIsTable = el.nextElementSibling?.tagName?.toLowerCase() === 'table';
          const caption = nextIsTable ? parseTableCaptionLine(plain) : null;
          if (caption) {
            children.push(
              new DocxParagraph({
                children: [
                  new TextRun({ text: `${caption.label} `, bold: true }),
                  new TextRun({ text: caption.title, bold: true }),
                ],
                style: 'ICFCaption',
              })
            );
            return;
          }
          // A paragraph immediately after a <table> whose text looks like a footnote marker
          // (asterisk/dagger/lettered note/"Abbreviations:") gets the smaller footnote style.
          const prevIsTable = el.previousElementSibling?.tagName?.toLowerCase() === 'table';
          const looksLikeFootnote =
            prevIsTable && /^([*†‡§]|\(?[a-h]\)?[.)]|note:|abbreviations?:)/i.test(plain);
          children.push(
            new DocxParagraph({
              children: runs,
              style: looksLikeFootnote ? 'ICFFootnote' : 'ICFBody',
            })
          );
        }
        return;
      }

      // Nested layout wrappers: descend so question headings / paragraphs inside are kept
      if (tag === 'div' || tag === 'section' || tag === 'article' || tag === 'blockquote') {
        if (hasBlockChild(el)) {
          el.childNodes.forEach(walk);
          return;
        }
        const plain = normalizeText(el.textContent || '');
        if (
          !skippedLeadingTitle &&
          sectionTitle &&
          children.length === 0 &&
          plain &&
          icfTitlesMatch(plain, sectionTitle)
        ) {
          skippedLeadingTitle = true;
          return;
        }
        const runs = buildRuns(el);
        if (runs.length) {
          children.push(
            new DocxParagraph({
              children: runs,
              style: 'ICFBody',
            })
          );
        }
        return;
      }

      if (tag === 'ul' || tag === 'ol') {
        const isOrdered = tag === 'ol';
        el.querySelectorAll(':scope > li').forEach((li, liIdx) => {
          const bullet = isOrdered ? `${liIdx + 1}. ` : '• ';
          if (hasBlockChild(li)) {
            // Nested blocks inside <li> (e.g. heading + paragraphs) — export each block
            let prefixed = false;
            li.childNodes.forEach((cn) => {
              if (cn.nodeType === Node.TEXT_NODE) {
                const t = normalizeText(cn.textContent || '');
                if (!t) return;
                children.push(
                  new DocxParagraph({
                    children: [new TextRun(prefixed ? '' : bullet), new TextRun(t)],
                    style: 'ICFList',
                  })
                );
                prefixed = true;
                return;
              }
              if (cn.nodeType !== Node.ELEMENT_NODE) return;
              const childTag = String(cn.tagName || '').toLowerCase();
              if (BLOCK_TAGS.has(childTag)) {
                walk(cn);
                prefixed = true;
                return;
              }
              const t = normalizeText(cn.textContent || '');
              if (!t) return;
              const isBold = childTag === 'strong' || childTag === 'b';
              children.push(
                new DocxParagraph({
                  children: [
                    new TextRun(prefixed ? '' : bullet),
                    new TextRun(isBold ? { text: t, bold: true } : t),
                  ],
                  style: 'ICFList',
                })
              );
              prefixed = true;
            });
            return;
          }
          const runs = buildRuns(li);
          if (runs.length) {
            children.push(
              new DocxParagraph({
                children: [new TextRun(bullet), ...runs],
                style: 'ICFList',
              })
            );
          }
        });
        return;
      }

      if (tag === 'table') {
        const rows = Array.from(el.querySelectorAll('tr')).map(
          (tr, rowIdx) =>
            new DocxTableRow({
              children: Array.from(tr.querySelectorAll('th,td')).map((cell) => {
                const isHeader = cell.tagName.toLowerCase() === 'th' || rowIdx === 0;
                return new DocxTableCell({
                  width: { size: 100 / (tr.children.length || 1), type: WidthType.PERCENTAGE },
                  children: [
                    new DocxParagraph({
                      text: normalizeText(cell.textContent || ''),
                      style: isHeader ? 'ICFTableHeader' : 'ICFTableCell',
                    }),
                  ],
                });
              }),
            })
        );
        children.push(
          new DocxTable({
            rows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
        return;
      }

      if (tag === 'br') {
        children.push(new DocxParagraph({ text: '', style: 'ICFBody' }));
        return;
      }

      if (tag === 'hr') {
        children.push(new DocxParagraph({ text: '─'.repeat(40), style: 'ICFBody' }));
        return;
      }

      // Fallback: descend so nested Q&A content is not lost
      el.childNodes.forEach(walk);
    };

    Array.from(container.childNodes).forEach(walk);
    return children.length ? children : [new DocxParagraph('')];
  };

  // Builds the ICF DOCX as a Blob without triggering a download; shared by the
  // DOCX export and the PDF export (which converts this same DOCX server-side).
  const buildIcfDocxBlob = async (sectionsToExport) => {
    const sectionChildren = [];
    sectionsToExport.forEach((section, idx) => {
      if (idx > 0) {
        sectionChildren.push(new DocxParagraph({ children: [], pageBreakBefore: true }));
      }
      // Title once as ICFHeading; strip the same title from body HTML if present
      const sectionTitle = section.title || 'Untitled Section';
      sectionChildren.push(new DocxParagraph({ text: sectionTitle, style: 'ICFHeading' }));
      const cleanedHtml = stripLeadingDuplicateSectionTitle(section.output || '', sectionTitle);
      sectionChildren.push(...htmlToDocxChildren(cleanedHtml, sectionTitle));
    });

    // ICF Formatting Specifications:
    // - Font: Arial, Calibri, Verdana (sans-serif preferred) or Times New Roman
    // - Font Size: 12pt for body text, 14pt for headings
    // - Headings: Clear, bold subheadings (14pt)
    // - Spacing: 1.15 line spacing, avoiding single spacing in large paragraphs
    // - Margins: Standard 1-inch (2.54 cm) on all sides
    // - Bullet Points: Use bullet points for lists
    // - Active Voice: Second person ("You will receive...")
    // - Tables: Use tables for schedules of visits or complex dosing regimens
    // - Language: 6th to 8th-grade reading level

    const doc = new Document({
      styles: {
        paragraphStyles: [
          {
            id: 'ICFHeading',
            name: 'ICF Heading',
            basedOn: 'Normal',
            next: 'Normal',
            run: { 
              size: 28, // 14pt (recommended for headings)
              bold: true,
              font: 'Arial' // Sans-serif preferred for screen reading
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 240, // 12pt spacing after heading
                before: 120, // 6pt spacing before heading
                line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
              }
            }
          },
          {
            id: 'ICFSubheading',
            name: 'ICF Subheading',
            basedOn: 'Normal',
            next: 'Normal',
            run: { 
              size: 28, // 14pt (subheading size)
              bold: true,
              font: 'Arial'
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 180, // 9pt spacing after subheading
                before: 120 // 6pt spacing before subheading
              },
              line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
            }
          },
          {
            id: 'ICFBody',
            name: 'ICF Body',
            basedOn: 'Normal',
            next: 'ICFBody',
            run: { 
              size: 24, // 12pt (body text)
              font: 'Arial' // Sans-serif preferred for screen reading
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 120, // 6pt spacing after paragraph
                before: 0,
                line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
              }
            }
          },
          {
            id: 'ICFBodySmall',
            name: 'ICF Body Small',
            basedOn: 'Normal',
            next: 'ICFBody',
            run: { 
              size: 24, // 12pt (minimum size)
              font: 'Arial'
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 120,
                before: 0
              },
              line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
            }
          },
          {
            id: 'ICFList',
            name: 'ICF List',
            basedOn: 'ICFBody',
            next: 'ICFBody',
            run: { 
              size: 24, // 12pt (body text size)
              font: 'Arial'
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 60, // 3pt spacing after list item
                before: 0
              },
              indent: {
                left: 720, // 0.5 inch indent for bullet points
                hanging: 360 // Hanging indent for bullet
              },
              line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
            }
          },
          {
            // "Optional Subheading" tier (h3+) called out in the ICF editorial standard —
            // visually subordinate to the 14pt Question Heading (ICFSubheading).
            id: 'ICFQuestionSubheading',
            name: 'ICF Question Subheading',
            basedOn: 'Normal',
            next: 'Normal',
            run: {
              size: 24, // 12pt
              bold: true,
              font: 'Arial'
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              spacing: {
                after: 120,
                before: 120,
                line: Math.round(1.15 * 240)
              }
            }
          },
          {
            id: 'ICFCaption',
            name: 'ICF Caption',
            basedOn: 'Normal',
            next: 'Normal',
            run: {
              size: 24, // 12pt, matches ICFTableHeader
              bold: true,
              font: 'Arial'
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              spacing: {
                after: 60,
                before: 120,
                line: Math.round(1.15 * 240)
              }
            }
          },
          {
            id: 'ICFFootnote',
            name: 'ICF Footnote',
            basedOn: 'Normal',
            next: 'Normal',
            run: {
              size: 20, // 10pt
              font: 'Arial'
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              spacing: {
                after: 60,
                before: 60,
                line: Math.round(1.15 * 240)
              }
            }
          },
          {
            id: 'ICFTableHeader',
            name: 'ICF Table Header',
            basedOn: 'Normal',
            next: 'Normal',
            run: { 
              size: 24, // 12pt
              bold: true,
              font: 'Arial'
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 60,
                before: 60
              },
              line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
            }
          },
          {
            id: 'ICFTableCell',
            name: 'ICF Table Cell',
            basedOn: 'Normal',
            next: 'Normal',
            run: { 
              size: 24, // 12pt (body text size)
              font: 'Arial'
            },
            paragraph: { 
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: 60,
                before: 60
              },
              line: Math.round(1.15 * 240) // 1.15 line spacing (240 twips = 1.0 spacing)
            }
          }
        ]
      },
      sections: [{ 
        properties: { 
          page: {
            margin: {
              top: 1440,    // 1 inch = 1440 twips (20 * 72)
              right: 1440,  // 1 inch = 1440 twips
              bottom: 1440, // 1 inch = 1440 twips
              left: 1440    // 1 inch = 1440 twips
            }
          }
        }, 
        children: sectionChildren 
      }]
    });
    return Packer.toBlob(doc);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportToWord = async (sectionsToExport) => {
    const blob = await buildIcfDocxBlob(sectionsToExport);
    downloadBlob(blob, 'icf-protocol.docx');
  };

  // Native, fully client-side PDF export (jsPDF) — no server round trip, so it
  // works regardless of whether LibreOffice/Graph conversion is available.
  const exportToPdf = (sectionsToExport) => {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = margin;

      const ensureSpace = (needed = 10) => {
        if (yPos + needed > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }
      };

      const addText = (text, fontSize = 12, isBold = false, spacing = 6) => {
        const normalized = normalizePdfText(text);
        if (!normalized) return;

        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');

        const lines = doc.splitTextToSize(normalized, contentWidth);
        const lineArray = Array.isArray(lines) ? lines : [lines];
        if (!lineArray.length) return;

        const lineHeight = fontSize * 0.45;
        lineArray.forEach((line) => {
          ensureSpace(lineHeight + spacing);
          doc.text(line, margin, yPos);
          yPos += lineHeight;
        });
        yPos += spacing;
      };

      const BLOCK_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'td', 'th', 'pre', 'blockquote']);
      const SKIP_TAGS = new Set(['script', 'style', 'thead', 'tbody', 'tfoot', 'colgroup', 'col']);

      const renderSectionHtml = (html, sectionTitle = '') => {
        const container = document.createElement('div');
        container.innerHTML = html || '';

        // Second-layer safety net: stripLeadingDuplicateSectionTitle already removes
        // an exact leading duplicate, but some content shapes slip past it. Mirror
        // htmlToDocxChildren's guard so the PDF stays in sync with the DOCX export —
        // skip the very first emitted block if its text duplicates the section title.
        let skippedLeadingTitle = false;
        let emittedAny = false;
        const isFirstAndDuplicateTitle = (text) => {
          if (skippedLeadingTitle || emittedAny || !sectionTitle || !text) return false;
          if (icfTitlesMatch(text, sectionTitle)) {
            skippedLeadingTitle = true;
            return true;
          }
          return false;
        };

        const walk = (node) => {
          if (node.nodeType === Node.TEXT_NODE) return;
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          const tag = node.tagName?.toLowerCase();
          if (SKIP_TAGS.has(tag)) {
            Array.from(node.childNodes).forEach(walk);
            return;
          }
          if (/^h[1-6]$/.test(tag)) {
            const text = extractPlainTextForPdf(node);
            if (isFirstAndDuplicateTitle(text)) return;
            const size = tag === 'h1' ? 15 : (tag === 'h2' || tag === 'h3') ? 13 : 12;
            const spacing = tag === 'h1' ? 7 : (tag === 'h2' || tag === 'h3') ? 5 : 4;
            addText(text, size, true, spacing);
            emittedAny = true;
            return;
          }
          if (tag === 'table') {
            const allRows = Array.from(node.querySelectorAll('tr'));
            if (!allRows.length) return;
            const firstRowIsHeader = allRows[0].querySelectorAll('th').length > 0;
            const head = firstRowIsHeader
              ? [Array.from(allRows[0].querySelectorAll('th,td')).map((cell) => extractPlainTextForPdf(cell))]
              : [];
            const bodyRowEls = firstRowIsHeader ? allRows.slice(1) : allRows;
            const body = bodyRowEls
              .map((row) => Array.from(row.querySelectorAll('th,td')).map((cell) => extractPlainTextForPdf(cell)))
              .filter((row) => row.some(Boolean));
            if (!head.length && !body.length) return;

            ensureSpace(20);
            autoTable(doc, {
              startY: yPos,
              margin: { left: margin, right: margin, bottom: margin },
              tableWidth: contentWidth,
              head,
              body,
              theme: 'grid',
              styles: { font: 'helvetica', fontSize: 10, cellPadding: 3, overflow: 'linebreak' },
              headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
            });
            yPos = doc.lastAutoTable.finalY + 6;
            emittedAny = true;
            return;
          }
          if (tag === 'ul' || tag === 'ol') {
            node.querySelectorAll(':scope > li').forEach((li) => {
              addText(`- ${extractPlainTextForPdf(li)}`, 12, false, 2);
              emittedAny = true;
            });
            return;
          }
          if (tag === 'br') { yPos += 4; return; }
          if (BLOCK_TAGS.has(tag)) {
            const text = extractPlainTextForPdf(node);
            if (isFirstAndDuplicateTitle(text)) return;
            addText(text, 12, false, 4);
            emittedAny = true;
            return;
          }
          if (tag === 'div') {
            const hasBlockChild = Array.from(node.children).some((child) =>
              BLOCK_TAGS.has(child.tagName?.toLowerCase()) || child.tagName?.toLowerCase() === 'table'
            );
            if (!hasBlockChild) {
              const text = extractPlainTextForPdf(node);
              if (isFirstAndDuplicateTitle(text)) return;
              addText(text, 12, false, 4);
              emittedAny = true;
              return;
            }
          }
          Array.from(node.childNodes).forEach(walk);
        };

        Array.from(container.childNodes).forEach(walk);
      };

      sectionsToExport.forEach((section, idx) => {
        if (idx > 0) {
          doc.addPage();
          yPos = margin;
        }
        const sectionTitle = section.title || 'Untitled Section';
        addText(sectionTitle, 14, true, 8);
        const cleanedHtml = stripLeadingDuplicateSectionTitle(section.output || '', sectionTitle);
        renderSectionHtml(cleanedHtml, sectionTitle);
      });

      doc.save('icf-protocol.pdf');
    } catch (err) {
      console.error('Failed to export ICF as PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const exportToXML = (sectionsToExport) => {
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<document>\n';
    // Sort sections by title (e.g., "1. Introduction", "1.1 Purpose")
    const sortedSections = [...sectionsToExport].sort((a, b) => {
      const aTitle = a.title || '';
      const bTitle = b.title || '';
      return aTitle.localeCompare(bTitle, undefined, { numeric: true });
    });

    for (const section of sortedSections) {
      if (!section.title || !section.output) continue; // Skip if no title or output
      const match = section.title.match(/^(\d+)(\.\d+)*/); // Extract main number (e.g., "1", "1.1")
      const mainNum = match ? match[0] : '';
      const sectionTitle = section.title.replace(/^(\d+(\.\d+)*\.?\s*)/, '').trim(); // Remove number prefix
      const tagName = toCamelCase(sectionTitle) || 'section';

      const parsed = parseSectionContent(section.output, mainNum);
      const sectionObj = {
        number: mainNum,
        title: sectionTitle,
        main: parsed.main,
        subsections: parsed.subsections
      };
      xmlContent += jsonToXml(sectionObj, tagName, '  ') + '\n';
    }
    xmlContent += '</document>';

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'icf_protocol.xml');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleViewVersionHistory = (_id) => {
    navigate(`/icf-section-history/${_id}?projectId=${effectiveProjectId}`);
  };

  const handleAddSection = async () => {
    const title = addDialogTab === 0 ? selectedSectionTitle : customSectionTitle;
    if (!title) {
      alert('Please select or enter a section title.');
      return;
    }

    try {
      const sectionData = {
        title,
        output: '',
        projectId: effectiveProjectId,
        documentType: 'ICF', // Add documentType
        country: selectedCountry || '',
        site: selectedSite || '',
      };
      const response = await documentEditorService.createICFSection(sectionData, effectiveProjectId);
      setSections((prev) => [...prev, response.data]);
      setNeedsInitialGeneration(false);
      handleCloseAddDialog();
      // Re-fetch sections to ensure order and latest data
      fetchSections();
    } catch (error) {
      console.error('Error adding section:', error);
      alert('Failed to add section. It might already exist or there was a server error.');
    }
  };

  const handleRevertVersion = async (sectionId, versionData) => {
    try {
      const updatedSection = await documentEditorService.updateICFSection(
        sectionId,
        { output: versionData.content },
        effectiveProjectId // Pass projectId
      );
      setSections(sections.map(s => s._id === sectionId ? { ...s, output: updatedSection.data.output } : s));
      alert('ICF section reverted to selected version!');
    } catch (error) {
      console.error('Error reverting ICF section version:', error);
      alert('Failed to revert ICF section version.');
    }
  };

  const handleMenuOpen = (event, _id) => {
    setAnchorEl(event.currentTarget);
    setSelectedSectionId(_id);
  };

  const handleMenuClose = () => setAnchorEl(null);

  // Common synonyms for ICF/medical translations
  // Maps words to their canonical form for better matching
  const SYNONYM_GROUPS = {
    // Participation verbs
    'join': ['participate', 'take part', 'enroll', 'enter'],
    'participate': ['join', 'take part', 'enroll', 'enter'],
    'take': ['participate', 'join', 'enroll'],
    'part': ['participate', 'join'],
    
    // Options/choices
    'choice': ['option', 'alternative'],
    'choices': ['options', 'alternatives'],
    'option': ['choice', 'alternative'],
    'options': ['choices', 'alternatives'],
    
    // Medical care
    'regular': ['usual', 'normal', 'standard'],
    'usual': ['regular', 'normal', 'standard'],
    'standard': ['regular', 'usual', 'normal'],
    
    // Time references
    'previous': ['prior', 'earlier', 'before'],
    'prior': ['previous', 'earlier', 'before'],
    'earlier': ['previous', 'prior', 'before'],
    
    // Medical progression
    'grown': ['progressed', 'advanced', 'worsened'],
    'progressed': ['grown', 'advanced', 'worsened'],
    'advanced': ['progressed', 'grown'],
    
    // Treatment terms
    'cancer': ['tumor', 'malignancy'],
    'drug': ['medication', 'medicine', 'treatment'],
    'drugs': ['medications', 'medicines', 'treatments'],
    'treatment': ['therapy', 'care'],
    'treatments': ['therapies', 'care'],
    'therapy': ['treatment', 'care'],
    
    // Communication verbs
    'tell': ['inform', 'give information', 'discuss'],
    'talk': ['discuss', 'speak', 'consult'],
    'discuss': ['talk', 'speak', 'consult'],
    
    // Modals and auxiliaries
    'may': ['can', 'could', 'might'],
    'can': ['may', 'could', 'might'],
    'could': ['may', 'can', 'might'],
    'might': ['may', 'can', 'could'],
    
    // Common phrases
    'available': ['accessible', 'obtainable'],
    'receive': ['get', 'obtain'],
    'getting': ['receiving', 'obtaining'],
    'receiving': ['getting', 'obtaining'],
    
    // Study terms
    'study': ['trial', 'research'],
    'research': ['study', 'trial'],
    'trial': ['study', 'research'],
    
    // Care terms
    'care': ['treatment', 'therapy'],
    'comfortable': ['comfort', 'at ease'],
    'symptoms': ['signs', 'effects'],
  };

  // Get canonical form of a word (for synonym matching)
  const getCanonicalForm = (word) => {
    const normalized = word.toLowerCase().trim();
    
    // Check if word is in any synonym group
    for (const [canonical, synonyms] of Object.entries(SYNONYM_GROUPS)) {
      if (normalized === canonical || synonyms.includes(normalized)) {
        return canonical;
      }
    }
    
    return normalized;
  };

  // Normalize word for better matching (handles common variations)
  const normalizeWord = (word) => {
    // Remove common suffixes and normalize
    let normalized = word
      .replace(/['"]/g, '') // Remove quotes
      .replace(/[.,!?;:()\[\]{}]/g, '') // Remove punctuation
      .replace(/ing$/, '') // Remove -ing
      .replace(/ed$/, '') // Remove -ed
      .replace(/s$/, '') // Remove plural -s
      .replace(/es$/, '') // Remove plural -es
      .replace(/ly$/, '') // Remove -ly
      .toLowerCase();
    
    // Get canonical form for synonym matching
    return getCanonicalForm(normalized);
  };

  // Extract n-grams (phrases) from text
  const extractNGrams = (words, n) => {
    const ngrams = [];
    for (let i = 0; i <= words.length - n; i++) {
      ngrams.push(words.slice(i, i + n).join(' '));
    }
    return ngrams;
  };

  // Calculate Jaccard similarity between two sets
  const jaccardSimilarity = (set1, set2) => {
    if (set1.size === 0 && set2.size === 0) return 1;
    if (set1.size === 0 || set2.size === 0) return 0;
    
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    
    return intersection.size / union.size;
  };

  // Calculate semantic similarity for ICF translations
  // This focuses on meaning rather than exact word matching
  const calculateTextAccuracy = (originalText, retranslatedText) => {
    if (!originalText || !retranslatedText) return 0;
    
    // Strip HTML tags and normalize whitespace for comparison
    const stripHtml = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html || '';
      return tempDiv.textContent || tempDiv.innerText || '';
    };
    
    // Keep whitespace as-is so word indexing stays consistent with DOM text nodes
    const original = stripHtml(originalText).toLowerCase().trim();
    const retranslated = stripHtml(retranslatedText).toLowerCase().trim();
    
    if (original.length === 0 && retranslated.length === 0) return 100;
    if (original.length === 0 || retranslated.length === 0) return 0;
    
    // Split into sentences for sentence-level comparison
    const originalSentences = original.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const retranslatedSentences = retranslated.split(/[.!?]+/).filter(s => s.trim().length > 0);
    
    // Word-level comparison with normalization
    const originalWords = original.split(/\s+/).filter(w => w.length > 0);
    const retranslatedWords = retranslated.split(/\s+/).filter(w => w.length > 0);
    
    if (originalWords.length === 0 && retranslatedWords.length === 0) return 100;
    if (originalWords.length === 0 || retranslatedWords.length === 0) return 0;
    
    // 1. Normalized word overlap (accounts for different word forms)
    const originalNormalized = new Set(originalWords.map(normalizeWord));
    const retranslatedNormalized = new Set(retranslatedWords.map(normalizeWord));
    const normalizedWordSimilarity = jaccardSimilarity(originalNormalized, retranslatedNormalized) * 100;
    
    // 2. Bigram (2-word phrase) similarity (captures meaning better than single words)
    const originalBigrams = extractNGrams(originalWords, 2);
    const retranslatedBigrams = extractNGrams(retranslatedWords, 2);
    const originalBigramSet = new Set(originalBigrams);
    const retranslatedBigramSet = new Set(retranslatedBigrams);
    const bigramSimilarity = jaccardSimilarity(originalBigramSet, retranslatedBigramSet) * 100;
    
    // 3. Trigram (3-word phrase) similarity (captures longer phrases)
    const originalTrigrams = extractNGrams(originalWords, 3);
    const retranslatedTrigrams = extractNGrams(retranslatedWords, 3);
    const originalTrigramSet = new Set(originalTrigrams);
    const retranslatedTrigramSet = new Set(retranslatedTrigrams);
    const trigramSimilarity = jaccardSimilarity(originalTrigramSet, retranslatedTrigramSet) * 100;
    
    // 4. Sentence-level similarity (compare meaning of complete sentences)
    // This is more lenient and focuses on overall meaning
    let sentenceSimilarity = 0;
    if (originalSentences.length > 0 && retranslatedSentences.length > 0) {
      const minSentences = Math.min(originalSentences.length, retranslatedSentences.length);
      const maxSentences = Math.max(originalSentences.length, retranslatedSentences.length);
      
      // Compare corresponding sentences with multiple methods
      let sentenceMatches = 0;
      for (let i = 0; i < minSentences; i++) {
        const origSentWords = originalSentences[i].split(/\s+/).filter(w => w.length > 0);
        const retransSentWords = retranslatedSentences[i].split(/\s+/).filter(w => w.length > 0);
        
        if (origSentWords.length > 0 && retransSentWords.length > 0) {
          // Use normalized words with synonym matching
          const origSet = new Set(origSentWords.map(normalizeWord));
          const retransSet = new Set(retransSentWords.map(normalizeWord));
          
          // Calculate word-level similarity
          const wordSim = jaccardSimilarity(origSet, retransSet);
          
          // Calculate bigram similarity for the sentence
          const origSentBigrams = extractNGrams(origSentWords, 2);
          const retransSentBigrams = extractNGrams(retransSentWords, 2);
          const origSentBigramSet = new Set(origSentBigrams);
          const retransSentBigramSet = new Set(retransSentBigrams);
          const bigramSim = jaccardSimilarity(origSentBigramSet, retransSentBigramSet);
          
          // Combine word and phrase similarity for sentence
          // Weight phrase matching higher as it captures meaning better
          const sentSim = (wordSim * 0.4 + bigramSim * 0.6);
          sentenceMatches += sentSim;
        }
      }
      
      // Very lenient penalty for missing/extra sentences (only 10% reduction)
      const lengthPenalty = 0.9 + (minSentences / maxSentences) * 0.1;
      sentenceSimilarity = (sentenceMatches / maxSentences) * lengthPenalty * 100;
    } else {
      sentenceSimilarity = 0;
    }
    
    // 5. Character-level similarity (for overall structure)
    const charSimilarity = calculateLevenshteinSimilarity(original, retranslated);
    
    // 6. Length similarity (accounts for paraphrasing with more/less words)
    const lengthRatio = Math.min(originalWords.length, retranslatedWords.length) / 
                       Math.max(originalWords.length, retranslatedWords.length);
    const lengthSimilarity = lengthRatio * 100;
    
    // Combine metrics with weights optimized for ICF translation accuracy
    // Higher weight on phrase-level and sentence-level matching as they capture meaning better
    const accuracy = (
      normalizedWordSimilarity * 0.20 +      // Normalized words with synonyms: 20%
      bigramSimilarity * 0.30 +              // Bigrams: 30% (most important for meaning)
      trigramSimilarity * 0.20 +             // Trigrams: 20%
      sentenceSimilarity * 0.20 +            // Sentence-level: 20% (increased weight)
      charSimilarity * 0.05 +                // Character-level: 5%
      lengthSimilarity * 0.05                // Length similarity: 5%
    );
    
    // Apply multiple leniency boosts for ICF translations (meaning matters more than exact words)
    let finalAccuracy = accuracy;
    
    // Boost 1: If phrase similarity is high, boost the score
    const phraseAvg = (bigramSimilarity + trigramSimilarity) / 2;
    if (phraseAvg > 50) {
      // Boost by up to 15% if phrase matching is good
      const boost = Math.min(15, (phraseAvg - 50) / 3.33);
      finalAccuracy += boost;
    }
    
    // Boost 2: If sentence-level similarity is high, additional boost
    if (sentenceSimilarity > 60) {
      const boost = Math.min(10, (sentenceSimilarity - 60) / 4);
      finalAccuracy += boost;
    }
    
    // Boost 3: If both word and phrase similarity are decent, meaning is preserved
    if (normalizedWordSimilarity > 50 && phraseAvg > 50) {
      const boost = Math.min(8, (normalizedWordSimilarity + phraseAvg - 100) / 12.5);
      finalAccuracy += boost;
    }
    
    // Boost 4: If length is similar and phrases match well, it's likely accurate
    if (lengthSimilarity > 80 && phraseAvg > 55) {
      finalAccuracy += 5;
    }
    
    return Math.max(0, Math.min(100, Math.round(finalAccuracy * 100) / 100)); // Round to 2 decimal places
  };

  // Build highlighted HTML for re-translated text by comparing with original English
  // Preserves HTML structure while highlighting text differences
  const buildHighlightedRetranslatedHtml = (originalHtml, retranslatedHtml, sectionId) => {
    if (!originalHtml || !retranslatedHtml) return retranslatedHtml || '';

    // Check cache first
    const cacheKey = `${sectionId}_${originalHtml.substring(0, 50)}_${retranslatedHtml.substring(0, 50)}`;
    if (highlightedRetranslatedHtml[sectionId] && highlightedRetranslatedHtml[sectionId].cacheKey === cacheKey) {
      return highlightedRetranslatedHtml[sectionId].html;
    }

    // Strip HTML to get plain text for comparison
    const stripHtmlToText = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html || '';
      return (tempDiv.textContent || tempDiv.innerText || '').trim();
    };

    // Keep whitespace as-is; do not collapse, so indices match DOM text nodes
    const originalText = stripHtmlToText(originalHtml);
    const reText = stripHtmlToText(retranslatedHtml);

    if (!originalText || !reText) {
      // Cache and return original HTML if no text to compare
      setHighlightedRetranslatedHtml(prev => ({
        ...prev,
        [sectionId]: { html: retranslatedHtml, cacheKey }
      }));
      return retranslatedHtml;
    }

    const originalWords = originalText.split(/\s+/);
    const reWords = reText.split(/\s+/);

    const n = originalWords.length;
    const m = reWords.length;

    // Longest Common Subsequence (LCS) on normalized words
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i += 1) {
      for (let j = 1; j <= m; j += 1) {
        if (normalizeWord(originalWords[i - 1]) === normalizeWord(reWords[j - 1])) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    // Backtrack to find which reWords are part of the LCS
    const inLcs = new Array(m).fill(false);
    let i = n;
    let j = m;
    while (i > 0 && j > 0) {
      if (normalizeWord(originalWords[i - 1]) === normalizeWord(reWords[j - 1])) {
        inLcs[j - 1] = true;
        i -= 1;
        j -= 1;
      } else if (dp[i - 1][j] >= dp[i][j - 1]) {
        i -= 1;
      } else {
        j -= 1;
      }
    }

    // Preserve HTML structure while highlighting differences
    // Create a temporary DOM to work with the retranslated HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = retranslatedHtml;

    // Collect all text nodes first to avoid DOM modification issues
    const textNodes = [];
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent.trim()) {
        textNodes.push(node);
      }
    }

    // Process text nodes and map word positions
    let wordIndex = 0;
    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      const words = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();
      
      words.forEach(word => {
        if (word.trim()) {
          const isMatch = inLcs[wordIndex];
          wordIndex++;
          
          if (!isMatch) {
            // Create highlighted span for differing words
            const highlightSpan = document.createElement('span');
            highlightSpan.style.backgroundColor = '#fff3cd';
            highlightSpan.style.borderBottom = '1px solid #ffa000';
            highlightSpan.textContent = word;
            fragment.appendChild(highlightSpan);
          } else {
            // Create text node for matching words
            fragment.appendChild(document.createTextNode(word));
          }
        } else {
          // Preserve whitespace
          fragment.appendChild(document.createTextNode(word));
        }
      });
      
      // Replace the text node with the fragment
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });

    const highlightedHtml = tempDiv.innerHTML;

    // Cache the result
    setHighlightedRetranslatedHtml(prev => ({
      ...prev,
      [sectionId]: { html: highlightedHtml, cacheKey }
    }));

    return highlightedHtml;
  };

  // Calculate Levenshtein similarity (0-100)
  const calculateLevenshteinSimilarity = (str1, str2) => {
    const len1 = str1.length;
    const len2 = str2.length;
    
    if (len1 === 0 && len2 === 0) return 100;
    if (len1 === 0 || len2 === 0) return 0;
    
    // Create matrix
    const matrix = [];
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }
    
    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    
    const distance = matrix[len1][len2];
    const maxLen = Math.max(len1, len2);
    const similarity = ((maxLen - distance) / maxLen) * 100;
    
    return Math.max(0, Math.min(100, similarity));
  };

  // Re-translate translated text back to English and compare
  const handleRetranslateToEnglish = async (sectionId, translatedText) => {
    if (!translatedText || !translatedText.trim()) {
      alert('No translated text available to re-translate.');
      return;
    }
    
    if (!selectedLanguage) {
      alert('No language selected for re-translation.');
      return;
    }
    
    // ALWAYS check database first for existing accuracy - don't rely on state which may be empty on reload
    // Use the same context variables as when loading translations to ensure exact match
    const countryContext = selectedCountry || null;
    const siteContext = selectedSite || null;
    
    console.log(`🔍 Checking DB for accuracy: lang=${selectedLanguage}, version=${selectedVersion || 'latest'}, country=${countryContext || 'null'}, site=${siteContext || 'null'}`);
    
    try {
      const savedTranslations = await documentEditorService.getICFSectionTranslations(
        sectionId,
        effectiveProjectId,
        {
          language: selectedLanguage,
          version: selectedVersion || 'latest',
          country: countryContext,
          site: siteContext,
        }
      );
      
      if (savedTranslations && savedTranslations.length > 0) {
        const savedTranslation = savedTranslations[0];
        if (savedTranslation.accuracy !== null && savedTranslation.accuracy !== undefined) {
          console.log(`✅ Found accuracy in database for section ${sectionId}: ${savedTranslation.accuracy}%. Loading from DB (skipping recalculation). Version: ${savedTranslation.version || 'unknown'}, Country: ${savedTranslation.country || 'null'}, Site: ${savedTranslation.site || 'null'}`);
          
          // Load BOTH LLM and rule-based accuracies from database if they exist
          const accuracyLLM = savedTranslation.accuracyLLM || null;
          const accuracyRuleBased = savedTranslation.accuracyRuleBased || null;
          
          const storedMethod = savedTranslation.accuracyExplanation?.includes('rule-based') ? 'rule-based' : 'llm';
          
          // Load from database into state with BOTH accuracy methods
          setRetranslatedSections(prev => ({
            ...prev,
            [sectionId]: {
              text: savedTranslation.retranslatedText || '',
              accuracy: savedTranslation.accuracy,
              accuracyLLM: accuracyLLM, // Load LLM accuracy from DB
              accuracyRuleBased: accuracyRuleBased, // Load rule-based accuracy from DB
              explanation: savedTranslation.accuracyExplanation || '',
              keyDifferences: savedTranslation.accuracyKeyDifferences || [],
              method: storedMethod
            }
          }));
          
          setAccuracyMethod(prev => ({
            ...prev,
            [sectionId]: storedMethod
          }));
          
          setShowAccuracy(prev => ({ ...prev, [sectionId]: true }));
          setShowRetranslatedText(prev => ({ ...prev, [sectionId]: false }));
          
          return; // Exit - no need to recalculate or re-translate
        }
      }
    } catch (dbError) {
      console.warn('Could not check database for existing accuracy:', dbError);
      // Continue to calculate if DB check fails
    }
    
    // Also check state (in case it was just loaded but not yet in DB check above)
    const existingRetranslated = retranslatedSections[sectionId];
    if (existingRetranslated && existingRetranslated.accuracy !== undefined && existingRetranslated.accuracy !== null) {
      console.log(`✅ Accuracy already exists in state for section ${sectionId}: ${existingRetranslated.accuracy}%. Using stored data.`);
      // Just show the existing accuracy - no need to recalculate
      setShowAccuracy(prev => ({ ...prev, [sectionId]: true }));
      return;
    }
    
    setIsRetranslating(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      // Re-translate the translated text back to English using the new endpoint
      const response = await documentEditorService.retranslateToEnglish(
        effectiveProjectId,
        translatedText,
        selectedLanguage
      );
      
      const retranslatedText = response?.translation || '';
      
      // Get original English text
      const section = sections.find(s => s._id === sectionId);
      const originalText = section?.output || '';
      
      // Calculate accuracy using selected method (LLM or rule-based)
      let accuracy = 0;
      let explanation = '';
      let keyDifferences = [];
      
      // Get the selected accuracy method for this section (default to 'llm')
      const selectedMethod = accuracyMethod[sectionId] || 'llm';
      
      if (selectedMethod === 'llm') {
        // Use LLM-based accuracy calculation (semantic understanding)
        try {
          const accuracyResponse = await documentEditorService.calculateTranslationAccuracy(
            effectiveProjectId,
            originalText,
            retranslatedText
          );
          
          accuracy = accuracyResponse.accuracy || 0;
          explanation = accuracyResponse.explanation || '';
          keyDifferences = accuracyResponse.keyDifferences || [];
          
          console.log(`✅ LLM-based accuracy calculated: ${accuracy}%`);
          if (explanation) {
            console.log(`📝 Explanation: ${explanation}`);
          }
        } catch (accuracyError) {
          console.error('Error calculating LLM accuracy, falling back to rule-based:', accuracyError);
          // Fallback to rule-based calculation if LLM fails
          accuracy = calculateTextAccuracy(originalText, retranslatedText);
          explanation = 'Accuracy calculated using rule-based method (LLM calculation failed)';
        }
      } else {
        // Use rule-based accuracy calculation (word matching/similarity)
        accuracy = calculateTextAccuracy(originalText, retranslatedText);
        explanation = 'Accuracy calculated using rule-based method (word matching and similarity analysis)';
        keyDifferences = [];
        console.log(`✅ Rule-based accuracy calculated: ${accuracy}%`);
      }
      
      // Store re-translated text and accuracy (store both LLM and rule-based accuracies)
      setRetranslatedSections(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId], // Preserve existing accuracies
          text: retranslatedText,
          accuracy: accuracy, // Current accuracy (for display)
          accuracyLLM: selectedMethod === 'llm' ? accuracy : (prev[sectionId]?.accuracyLLM || null),
          accuracyRuleBased: selectedMethod === 'rule-based' ? accuracy : (prev[sectionId]?.accuracyRuleBased || null),
          explanation: explanation,
          keyDifferences: keyDifferences,
          method: selectedMethod // Store which method was used for current accuracy
        }
      }));
      
      // Show accuracy by default when calculated, but keep language version visible initially
      setShowAccuracy(prev => ({
        ...prev,
        [sectionId]: true
      }));
      // Don't automatically switch to retranslated text - keep language version visible
      setShowRetranslatedText(prev => ({
        ...prev,
        [sectionId]: false
      }));
      
      // Save accuracy to database (use same context variables for consistency)
      try {
        await documentEditorService.saveTranslationAccuracy(
          effectiveProjectId,
          sectionId,
          selectedLanguage,
          selectedVersion || 'latest',
          countryContext,
          siteContext,
          {
            accuracy: accuracy,
            explanation: explanation,
            keyDifferences: keyDifferences,
            retranslatedText: retranslatedText,
            method: selectedMethod // Pass the method so both accuracies are stored separately
          }
        );
        console.log(`✅ Accuracy saved to database for section ${sectionId}`);
      } catch (saveError) {
        console.error('Error saving accuracy to database:', saveError);
        // Don't block the UI if save fails
      }
      
      console.log(`✅ Re-translation complete for section ${sectionId}. Accuracy: ${accuracy}%`);
      
    } catch (err) {
      console.error('Error re-translating to English:', err);
      alert('Failed to re-translate. Please try again.');
    } finally {
      setIsRetranslating(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Pre-select the first available title or clear custom title
    setSelectedSectionTitle(availableSectionTitles[0] || '');
    setCustomSectionTitle('');
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedSectionTitle('');
    setCustomSectionTitle('');
    setAddDialogTab(0); // Reset to template tab
  };

  const handleSectionTitleChange = (event) => {
    setSelectedSectionTitle(event.target.value);
    setCustomSectionTitle(''); // Clear custom title if a predefined one is selected
  };

  const handleCustomTitleChange = (event) => {
    setCustomSectionTitle(event.target.value);
    setSelectedSectionTitle(''); // Clear predefined title if a custom one is entered
  };

  const openTranslateDialogForSection = (section) => {
    setTranslateState({
      open: true,
      language: SUPPORTED_TRANSLATION_LANGUAGES[0],
      customLanguage: '',
      translation: '',
      loading: false,
      sectionId: section._id,
      sectionTitle: section.title,
      error: null,
    });
  };

  const closeTranslateDialog = () => {
    setTranslateState((prev) => ({
      ...prev,
      open: false,
      translation: '',
      loading: false,
      sectionId: null,
      sectionTitle: '',
      error: null,
    }));
  };

  const resolveTargetLanguage = () => {
    if (translateState.language === 'Custom') {
      return translateState.customLanguage.trim();
    }
    return translateState.language;
  };

  const handlePerformTranslation = async () => {
    if (!translateState.sectionId || !effectiveProjectId) return;
    const targetLanguage = resolveTargetLanguage();
    if (!targetLanguage) {
      setTranslateState((prev) => ({
        ...prev,
        error: 'Please select or enter a target language.',
      }));
      return;
    }

    setTranslateState((prev) => ({ ...prev, loading: true, error: null, translation: '' }));
    try {
      const response = await documentEditorService.translateICFSection({
        sectionId: translateState.sectionId,
        projectId: effectiveProjectId,
        targetLanguage,
        baseCountry: selectedCountry,
      });
      setTranslateState((prev) => ({
        ...prev,
        translation: response?.translation || '',
        loading: false,
      }));
    } catch (err) {
      console.error('Error translating ICF section:', err);
      setTranslateState((prev) => ({
        ...prev,
        error:
          err?.message ||
          err?.response?.data?.message ||
          'Failed to translate the section. Please try again.',
        loading: false,
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setEditorTab(newValue);
  };

  const handleMainPageTabChange = (_event, newValue) => {
    setMainPageTab(newValue);
  };

  /** Leave country / site / translation URL context and open the project main ICF. */
  const handleReturnToMainICF = () => {
    const projectId = effectiveProjectId || projectIdFromUrl;
    if (!projectId) return;
    navigate(`/document-editor/study-form?project=${projectId}&tab=icf`);
    setMainPageTab(0);
    setEditorTab(0);
  };

  const openCountryDialog = () => {
    setNewCountryName('');
    setCountryNameError('');
    setCountryCloneSource('base');
    if (countrySelectionOptions && countrySelectionOptions.length) {
      setCountryNameMode('selected');
      setNewCountryName(countrySelectionOptions[0] || '');
    } else {
      setCountryNameMode('custom');
    }
    setIsCountryDialogOpen(true);
  };

  const closeCountryDialog = () => {
    setIsCountryDialogOpen(false);
    setCountryNameError('');
    setCountryCloneSource('base');
  };

  const openSiteDialog = () => {
    const preferredIndia = countryEntries.find(
      (entry) => buildCountryKey(entry.name) === 'india'
    );
    const defaultCountry =
      selectedCountry ||
      siteCountryForDialog ||
      preferredIndia?.name ||
      countryEntries[0]?.name ||
      'India';
    setNewSiteName('');
    setSiteNameError('');
    setSiteCountryForDialog(defaultCountry);
    setIsSiteDialogOpen(true);
  };

  const closeSiteDialog = () => {
    setIsSiteDialogOpen(false);
    setSiteNameError('');
  };

  const buildCountryKey = (name = '') => name.trim().toLowerCase();
  const buildSiteKey = (name = '') => name.trim().toLowerCase();

  const buildInitialCountryVersion = (name = '') => {
    const normalized = name.trim().replace(/\s+/g, '_');
    return `v1_${normalized || 'country'}`;
  };

  const buildInitialSiteVersion = (name = '') => {
    const normalized = name.trim().replace(/\s+/g, '_');
    return `v1_${normalized || 'site'}`;
  };

  const handleCountrySubmit = async () => {
    const trimmed = newCountryName.trim();
    if (!trimmed) {
      setCountryNameError('Country name is required.');
      return;
    }
    const key = buildCountryKey(trimmed);
    const existingEntry = countryEntries.find((entry) => buildCountryKey(entry.name) === key);
    if (existingEntry) {
      closeCountryDialog();
      handleCountryRowClick(existingEntry.name);
      return;
    }

    if (!effectiveProjectId) {
      setCountryNameError('A project must be selected before adding a country.');
      return;
    }

    try {
      setCountryEntriesLoading(true);
      setCountryEntriesError(null);
      const cloneSourceEntry =
        countryCloneSource === 'base'
          ? null
          : countryEntries.find((entry) => buildCountryKey(entry.name) === countryCloneSource);
      const sourceCountry = cloneSourceEntry?.name || null;
      const sourceVersionLabel = cloneSourceEntry?.latestVersionLabel || null;

      const response = await documentEditorService.createICFCountryFromBase(
        effectiveProjectId,
        trimmed,
        {
          sourceCountry,
          sourceVersionLabel,
        }
      );
      await refreshCountryEntries();
      closeCountryDialog();
      handleCountryRowClick(trimmed);
    } catch (error) {
      console.error('Error creating country-specific ICF copy:', error);
      setCountryNameError(
        error?.message || 'Failed to create country-specific ICF. Please try again.'
      );
    } finally {
      setCountryEntriesLoading(false);
    }
  };

  const handleSiteSubmit = async () => {
    const trimmedSite = newSiteName.trim();
    const trimmedCountry = (siteCountryForDialog || '').trim();

    if (!trimmedCountry) {
      setSiteNameError('Country is required for site-specific ICFs.');
      return;
    }

    if (!trimmedSite) {
      setSiteNameError('Hospital / site name is required.');
      return;
    }

    const siteKey = buildSiteKey(trimmedSite);
    const countryKey = buildCountryKey(trimmedCountry);

    const existingEntry = siteEntries.find(
      (entry) =>
        buildCountryKey(entry.country || '') === countryKey &&
        buildSiteKey(entry.site || '') === siteKey
    );
    if (existingEntry) {
      closeSiteDialog();
      handleSiteRowClick(existingEntry.country, existingEntry.site);
      return;
    }

    if (!effectiveProjectId) {
      setSiteNameError('A project must be selected before adding a site.');
      return;
    }

    try {
      setSiteEntriesLoading(true);
      setSiteEntriesError(null);
      const sourceForClone = trimmedCountry || 'India';
      await documentEditorService.createICFSiteFromCountry(effectiveProjectId, {
        country: trimmedCountry,
        site: trimmedSite,
        sourceCountry: sourceForClone || 'India',
      });
      await refreshSiteEntries();
      await refreshCountryEntries();
      closeSiteDialog();
      handleSiteRowClick(trimmedCountry, trimmedSite);
    } catch (error) {
      console.error('Error creating site-specific ICF copy:', error);
      setSiteNameError(
        error?.message || 'Failed to create site-specific ICF. Please try again.'
      );
    } finally {
      setSiteEntriesLoading(false);
    }
  };

  const handleCountryRowClick = (countryName) => {
    if (!countryName) return;
    const projectParam = effectiveProjectId || projectIdFromUrl;
    if (!projectParam || !resolvedDocumentId) {
      console.warn('Missing project or document id for navigation', {
        projectParam,
        documentId: resolvedDocumentId,
      });
      return;
    }
    console.log('Navigating to country ICF', {
      countryName,
      projectParam,
      documentId: resolvedDocumentId,
      currentPath: location.pathname + location.search,
    });
    const encodedCountry = encodeURIComponent(countryName);
    navigate(
      `/document-editor/study-form?project=${projectParam}&tab=icf&country=${encodedCountry}`
    );
    setMainPageTab(0);
  };

  const handleSiteRowClick = (countryName, siteName, language = null, version = null) => {
    if (!countryName || !siteName) return;
    const projectParam = effectiveProjectId || projectIdFromUrl;
    if (!projectParam || !resolvedDocumentId) {
      console.warn('Missing project or document id for navigation', {
        projectParam,
        documentId: resolvedDocumentId,
      });
      return;
    }
    const encodedCountry = encodeURIComponent(countryName);
    const encodedSite = encodeURIComponent(siteName);
    let url = `/document-editor/study-form?project=${projectParam}&tab=icf&country=${encodedCountry}&site=${encodedSite}`;
    if (language) {
      url += `&language=${encodeURIComponent(language)}`;
    }
    if (version) {
      url += `&version=${encodeURIComponent(version)}`;
    }
    navigate(url);
    setMainPageTab(0);
  };

  // Language management functions
  const openLanguageDialog = () => {
    setSelectedCountryForLanguage('');
    setSelectedSiteForLanguage('');
    setAvailableSitesForLanguage([]);
    setNewLanguage('');
    setCustomLanguageText('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    setLanguageError('');
    setIsLanguageDialogOpen(true);
  };

  const closeLanguageDialog = () => {
    setIsLanguageDialogOpen(false);
    setSelectedCountryForLanguage('');
    setSelectedSiteForLanguage('');
    setAvailableSitesForLanguage([]);
    setNewLanguage('');
    setCustomLanguageText('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    setLanguageError('');
  };

  // When country is selected, update available sites
  const handleCountrySelectForLanguage = (country) => {
    setSelectedCountryForLanguage(country);
    setSelectedSiteForLanguage('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    
    // Filter sites for selected country
    const sitesForCountry = siteEntries.filter(
      entry => entry.country === country
    );
    setAvailableSitesForLanguage(sitesForCountry);
  };

  // When site is selected, update available versions from MAIN ICF, COUNTRY, AND SITE
  // Only show valid, available versions (not random/invalid ones)
  const handleSiteSelectForLanguage = async (siteName) => {
    setSelectedSiteForLanguage(siteName);
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    
    // Find the site entry
    const siteEntry = siteEntries.find(
      entry => entry.site === siteName && entry.country === selectedCountryForLanguage
    );
    
    if (siteEntry && effectiveProjectId && selectedCountryForLanguage) {
      try {
        const versionSet = new Set();
        
        // Get latest versions from entries - ONLY from the selected country context
        // IMPORTANT: countryEntries only contains country-specific entries, not main/base ICF
        // So we only use versions from the selected country and site
        
        // Find the selected country entry (must match exactly by name)
        const countryEntry = countryEntries.find(
          entry => entry.name === selectedCountryForLanguage || entry.country === selectedCountryForLanguage
        );
        
        // Collect latest version labels from entries - ONLY from selected country/site
        const entryVersions = [];
        
        // 1. Selected country version (must match selectedCountryForLanguage exactly)
        // Double-check to ensure we're not getting versions from other countries
        if (countryEntry && 
            (countryEntry.name === selectedCountryForLanguage || countryEntry.country === selectedCountryForLanguage) &&
            countryEntry.latestVersionLabel) {
          entryVersions.push(countryEntry.latestVersionLabel);
        }
        
        // 2. Selected site version (must match selected site AND country exactly)
        if (siteEntry && 
            siteEntry.site === siteName && 
            siteEntry.country === selectedCountryForLanguage &&
            siteEntry.latestVersionLabel) {
          // Double-check the site entry matches the selected country
          if (siteEntry.country === selectedCountryForLanguage) {
            entryVersions.push(siteEntry.latestVersionLabel);
          }
        }
        
        // Find the highest version number from entries to use as a reference
        let maxVersionNum = 0;
        entryVersions.forEach(v => {
          const match = v.match(/v(\d+)/i);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxVersionNum) maxVersionNum = num;
          }
        });
        
        // Helper function to validate and add version - VERY STRICT
        const addValidVersion = (versionLabel) => {
          if (!versionLabel || typeof versionLabel !== 'string') return;
          
          const cleaned = versionLabel.trim();
          if (!cleaned) return;
          
          // Extract version number
          const versionMatch = cleaned.match(/v(\d+)/i);
          if (!versionMatch) return;
          
          const versionNum = parseInt(versionMatch[1]);
          
          // VERY STRICT FILTERING to prevent random versions like v39, v38, etc.:
          // 1. Absolute maximum cap at 10 - no versions above 10 allowed
          // 2. If we have maxVersionNum from entries, ONLY allow versions <= maxVersionNum (not +1)
          // 3. This prevents random high numbers completely
          const absoluteMax = 10; // Hard cap at 10
          
          if (versionNum >= 1 && versionNum <= absoluteMax) {
            // If we have a reference max version, be EXTREMELY strict - only allow <= maxVersionNum
            if (maxVersionNum > 0) {
              if (versionNum > maxVersionNum) {
                return; // Skip versions that are higher than the latest from entries
              }
            }
            versionSet.add(cleaned);
          }
        };
        
        // 1. Add latest versions from entries ONLY (these are always valid and current)
        // These are the PRIMARY and ONLY source - guaranteed to be valid
        // We don't fetch from sections to avoid random/invalid versions
        entryVersions.forEach(v => {
          if (v) {
            const cleaned = v.trim();
            if (cleaned) {
              // Directly add entry versions without validation (they're already valid)
              versionSet.add(cleaned);
            }
          }
        });
        
        // Note: We're NOT fetching versions from sections anymore to avoid random/invalid versions
        // Only use latestVersionLabel from entries which are the actual current versions
        
        // 3. If still no versions, use fallback (but only from selected country)
        if (versionSet.size === 0) {
          // Only use site entry if it matches the selected country
          if (siteEntry && 
              siteEntry.country === selectedCountryForLanguage &&
              siteEntry.latestVersionLabel) {
            versionSet.add(siteEntry.latestVersionLabel);
          } 
          // Only use country entry if it matches the selected country
          else if (countryEntry && 
                   (countryEntry.name === selectedCountryForLanguage || countryEntry.country === selectedCountryForLanguage) &&
                   countryEntry.latestVersionLabel) {
            versionSet.add(countryEntry.latestVersionLabel);
          } else {
            // Fallback to initial version for the site
            versionSet.add(buildInitialSiteVersion(siteName));
          }
        }
        
        // Sort versions: extract numbers and compare (newest first)
        const versions = Array.from(versionSet).sort((a, b) => {
          const numA = parseInt(a.match(/\d+/)?.[0] || '0');
          const numB = parseInt(b.match(/\d+/)?.[0] || '0');
          return numB - numA; // Descending order (newest first)
        });
        
        setAvailableVersionsForLanguage(versions);
      } catch (err) {
        console.error('Error fetching versions:', err);
        // Fallback: use latest version from entries only (selected country only)
        const versionSet = new Set();
        
        // Only use site entry if it matches the selected country
        if (siteEntry && 
            siteEntry.country === selectedCountryForLanguage &&
            siteEntry.latestVersionLabel) {
          versionSet.add(siteEntry.latestVersionLabel);
        }
        
        // Only use country entry if it matches the selected country
        const countryEntry = countryEntries.find(
          entry => entry.name === selectedCountryForLanguage || entry.country === selectedCountryForLanguage
        );
        if (countryEntry && 
            (countryEntry.name === selectedCountryForLanguage || countryEntry.country === selectedCountryForLanguage) &&
            countryEntry.latestVersionLabel) {
          versionSet.add(countryEntry.latestVersionLabel);
        }
        
        if (versionSet.size === 0) {
          versionSet.add(buildInitialSiteVersion(siteName));
        }
        
        setAvailableVersionsForLanguage(Array.from(versionSet));
      }
    }
  };

  const handleLanguageSubmit = async () => {
    const finalLanguage = newLanguage === 'Custom' ? customLanguageText.trim() : newLanguage.trim();
    
    if (!selectedCountryForLanguage) {
      setLanguageError('Please select a country.');
      return;
    }
    if (!selectedSiteForLanguage) {
      setLanguageError('Please select a site.');
      return;
    }
    if (!finalLanguage) {
      setLanguageError('Please select or enter a language.');
      return;
    }
    if (!selectedVersionForLanguage) {
      setLanguageError('Please select a version.');
      return;
    }
    if (!effectiveProjectId) {
      setLanguageError('Project ID is missing.');
      return;
    }

    try {
      setLanguagesLoading(true);
      setLanguageError('');
      
      await documentEditorService.createICFLanguage(effectiveProjectId, {
        country: selectedCountryForLanguage,
        site: selectedSiteForLanguage,
        language: finalLanguage,
        version: selectedVersionForLanguage,
      });
      
      // Refresh languages list
      await refreshLanguages();
      closeLanguageDialog();
    } catch (err) {
      console.error('Error creating language:', err);
      setLanguageError(err?.message || 'Failed to create language. Please try again.');
    } finally {
      setLanguagesLoading(false);
    }
  };

  const handleLanguageOpen = (languageEntry, isApproved = false) => {
    const projectId = effectiveProjectId || projectIdFromUrl;
    if (!projectId) {
      alert('Project is still loading. Please try again in a moment.');
      return;
    }
    if (!languageEntry?.country || !languageEntry?.site || !languageEntry?.language) {
      alert('This translation entry is missing country, site, or language.');
      return;
    }

    const encodedCountry = encodeURIComponent(languageEntry.country);
    const encodedSite = encodeURIComponent(languageEntry.site);
    const encodedLanguage = encodeURIComponent(languageEntry.language);
    const encodedVersion = encodeURIComponent(languageEntry.version || '');

    let url = `/document-editor/study-form?project=${projectId}&tab=icf&country=${encodedCountry}&site=${encodedSite}&language=${encodedLanguage}`;
    if (languageEntry.version) {
      url += `&version=${encodedVersion}`;
    }

    // Add fromApproved parameter if this is an approved translation
    if (isApproved || languageEntry.approved) {
      url += '&fromApproved=true';
    }

    navigate(url);
    setMainPageTab(0);
    setEditorTab(0);
  };

  const handleLanguageDelete = async (languageId) => {
    if (!effectiveProjectId) return;
    if (!window.confirm('Are you sure you want to delete this language entry?')) return;
    
    try {
      setLanguagesLoading(true);
      await documentEditorService.deleteICFLanguage(languageId, effectiveProjectId);
      await refreshLanguages();
    } catch (err) {
      console.error('Error deleting language:', err);
      alert(err?.message || 'Failed to delete language.');
    } finally {
      setLanguagesLoading(false);
    }
  };

  if ((mainPageTab === 0 && isLoading) || projectContextLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || projectContextError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || projectContextError}</Alert>
      </Box>
    );
  }

  if (!effectiveProjectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please select a project to view its documents.</Alert>
      </Box>
    );
  }

  const headingLabel =
    mainPageTab === 0
      ? selectedSite
        ? `ICF Editor — ${selectedCountry || 'Country'} — ${selectedSite}`
        : selectedCountry
          ? `ICF Editor — ${selectedCountry}`
          : 'ICF Editor'
      : 'ICF Plan';

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ flexGrow: 1, p: 3, mt:1.5}} className="icf-editor-container">
        <Typography variant="h4" gutterBottom sx={{ mt: -0.5 }}>
          {headingLabel}
        </Typography>

        <Tabs
          value={mainPageTab}
          onChange={handleMainPageTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 2 }}
          aria-label="Main ICF tabs"
        >
          <Tab label="Main ICF" value={0} />
          <Tab label="ICF Plan" value={1} />
        </Tabs>

        {mainPageTab === 0 ? (
          <>
            {needsInitialGeneration && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No ICF sections exist for this project yet. Click &quot;Generate ICF&quot; to create the standard template.
              </Alert>
            )}
            {(selectedCountry || selectedSite) && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" startIcon={<ArrowBackIcon />} onClick={handleReturnToMainICF}>
                    Main ICF
                  </Button>
                }
              >
                {selectedSite ? (
                  <>
                    You are editing the site-specific ICF for <strong>{selectedSite}</strong>
                    {selectedCountry ? (
                      <>
                        {' '}under <strong>{selectedCountry}</strong>
                      </>
                    ) : null}
                    . Changes and versions are tracked separately from the main and country-level ICFs.
                  </>
                ) : (
                  <>
                    You are editing the ICF for <strong>{selectedCountry}</strong>. Changes and versions are tracked separately from the main ICF.
                  </>
                )}
              </Alert>
            )}
            {!selectedCountry && !selectedSite && selectedLanguage && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" startIcon={<ArrowBackIcon />} onClick={handleReturnToMainICF}>
                    Main ICF
                  </Button>
                }
              >
                Viewing translation in <strong>{selectedLanguage}</strong>
                {selectedVersion ? <> (version: {selectedVersion})</> : null}.
                Use <strong>Main ICF</strong> to return to the base document.
              </Alert>
            )}
        <AppBar position="static" color="transparent" elevation={0} className="icf-appbar" sx={{ mb: 2 }}>
          <Toolbar variant="dense" sx={{ p: 1 }}>
            <Box className="icf-toolbar-surface" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {!isApprovedTranslation && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                          startIcon={isGeneratingAllSections ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
                          onClick={generateAllICFSections}
                          disabled={
                            isGeneratingAllSections ||
                            isValidatingICF ||
                            isLoading ||
                            !effectiveProjectId ||
                            Boolean(selectedCountry)
                          }
                    sx={{ mr: 1 }}
                    className="icf-btn-primary btn-3d"
                  >
                          {isGeneratingAllSections ? 'Generating ICF...' : 'Generate ICF'}
                  </Button>
                  <Tooltip title="Review the full ICF for duplication, consistency, and completeness; auto-correct content while preserving HTML formatting">
                    <Button
                      variant="outlined"
                      startIcon={isValidatingICF ? <CircularProgress size={18} /> : <FactCheckIcon />}
                      onClick={() => runICFSelfValidationAndApply()}
                      disabled={
                        isValidatingICF ||
                        isGeneratingAllSections ||
                        isLoading ||
                        !effectiveProjectId ||
                        Boolean(selectedCountry) ||
                        icfContentSections.length === 0
                      }
                      sx={{ mr: 1 }}
                    >
                      {isValidatingICF ? 'Optimizing ICF…' : 'Validate & Optimize ICF'}
                    </Button>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    sx={{ mr: 1 }}
                  >
                    Reset All Sections
                  </Button>
                  <Box sx={{ mr: 1 }}>
                    <StyleComplianceCheck
                      sections={icfContentSections}
                      documentType="ICF"
                      documentId={effectiveProjectId}
                      onSectionAutofixed={handleStyleAutofixApplied}
                      disabled={icfContentSections.length === 0}
                    />
                  </Box>
                  <Tooltip title={`${mode === 'edit' ? 'Switch to Preview Mode' : 'Switch to Edit Mode'} (Ctrl+P)`}>
                    <Button
                      variant="outlined"
                      startIcon={mode === 'edit' ? <VisibilityIcon /> : <EditIcon />}
                      onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')}
                      sx={{
                        mr: 1,
                        background: mode === 'edit' 
                          ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                          : 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                        color: 'white !important',
                        borderColor: mode === 'edit' ? '#1565c0' : '#388e3c',
                        transition: 'all 180ms ease',
                        '&:hover': {
                          background: mode === 'edit' 
                            ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%) !important'
                            : 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%) !important',
                          color: 'white !important',
                          borderColor: mode === 'edit' ? '#1565c0' : '#388e3c',
                          transform: 'translateY(-2px) scale(1.03)',
                          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      {mode === 'edit' ? 'Preview Mode' : 'Edit Mode'}
                    </Button>
                  </Tooltip>
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={isExportingPdf ? <CircularProgress size={16} /> : <ExportIcon />}
                endIcon={<ArrowDropDownIcon />}
                disabled={isExportingPdf}
                onClick={(event) => setExportAnchorEl(event.currentTarget)}
                sx={{
                  transition: 'all 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 8px 20px rgba(11, 116, 181, 0.25)',
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(11, 116, 181, 0.05)'
                  }
                }}
              >
                {isExportingPdf ? 'Exporting…' : 'Export'}
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={() => setExportAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExport('word');
                  }}
                >
                  <ExportIcon sx={{ mr: 1, fontSize: 20 }} />
                  Export to DOCX
                </MenuItem>
                <MenuItem
                  disabled={isExportingPdf}
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExport('pdf');
                  }}
                >
                  <ExportIcon sx={{ mr: 1, fontSize: 20 }} />
                  {isExportingPdf ? 'Exporting PDF…' : 'Export to PDF'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExport('xml');
                  }}
                >
                  <ExportIcon sx={{ mr: 1, fontSize: 20 }} />
                  Export to XML
                </MenuItem>
              </Menu>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid transparent' }} className="icf-section-card fade-in tilt-3d">
          <Tabs
                value={editorTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="icf editor tabs"
          >
            <Tab label="Sections" />
            <Tab label="Title page" />
          </Tabs>
        </Paper>
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={0}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Country-Specific ICF Library
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Create localized ICF copies per country. Selecting a country opens a dedicated editor with the current ICF content ready for localisation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Country Specific ICFs
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCountryDialog}
                  disabled={countryEntriesLoading}
                  className="icf-btn-primary btn-3d"
                >
                  Add Country
                </Button>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Latest Version</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {countryEntriesLoading && countryEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : countryEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No country-specific ICFs yet. Click &quot;Add Country&quot; to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      countryEntries.map((entry) => {
                        const latestVersion =
                          entry.latestVersionLabel || buildInitialCountryVersion(entry.name);
                        return (
                          <TableRow
                            key={entry.countryKey || entry.name}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleCountryRowClick(entry.name)}
                          >
                            <TableCell>{entry.name}</TableCell>
                            <TableCell>{latestVersion}</TableCell>
                            <TableCell>
                              <Button size="small" onClick={(ev) => { ev.stopPropagation(); handleCountryRowClick(entry.name); }}>
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                    {countryEntriesError && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'error.main' }}>
                          {countryEntriesError}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Site-Specific ICF Library
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Create hospital or investigator site copies that inherit from the latest country-specific ICF (currently defaulting to India when unavailable). Each site tracks its own edits and version history separately.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Site Specific ICFs
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openSiteDialog}
                  disabled={siteEntriesLoading || countryEntries.length === 0}
                  className="icf-btn-primary btn-3d"
                >
                  Add Site
                </Button>
              </Box>
              {countryEntries.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add at least one country-specific ICF before creating site-specific copies.
                </Alert>
              )}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Site / Hospital</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Latest Version</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {siteEntriesLoading && siteEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : siteEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No site-specific ICFs yet. Click &quot;Add Site&quot; to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      siteEntries.map((entry) => {
                        const latestVersion =
                          entry.latestVersionLabel || buildInitialSiteVersion(entry.site);
                        return (
                          <TableRow
                            key={`${entry.countryKey || entry.country || 'default'}-${entry.siteKey || entry.site}`}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleSiteRowClick(entry.country, entry.site)}
                          >
                            <TableCell>{entry.country || '—'}</TableCell>
                            <TableCell>{entry.site}</TableCell>
                            <TableCell>{latestVersion}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleSiteRowClick(entry.country, entry.site);
                                }}
                              >
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                    {siteEntriesError && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2, color: 'error.main' }}>
                          {siteEntriesError}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            
            {/* Languages Table */}
            <Paper sx={{ p: 2, borderRadius: 2, mt: 3 }} elevation={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {mainPageTab === 1 ? 'Approved Translations' : 'Languages'}
                </Typography>
                {mainPageTab === 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openLanguageDialog}
                    disabled={siteEntries.length === 0 || languagesLoading}
                    className="icf-btn-primary btn-3d"
                  >
                    Add Language
                  </Button>
                )}
              </Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {mainPageTab === 1 
                  ? 'View approved translations. Only approved translations are available in the ICF plan. Click "Open in Current Place" to view the translated ICF.'
                  : 'Manage language translations for site-specific ICFs. Select a language and version to view the translated ICF alongside the English version.'}
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 150 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {languagesLoading && languages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : languages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          {siteEntries.length === 0 
                            ? 'No site-specific ICFs available. Add a site first to manage languages.'
                            : 'No languages added yet. Click "Add Language" to create a translation.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      languages
                        .filter(langEntry => mainPageTab === 1 ? langEntry.approved === true : true) // Only show approved in ICF Plan tab
                        .map((langEntry) => (
                        <TableRow key={langEntry._id} hover>
                          <TableCell>{langEntry.country || '—'}</TableCell>
                          <TableCell>{langEntry.site || '—'}</TableCell>
                          <TableCell>{langEntry.language}</TableCell>
                          <TableCell>{langEntry.version}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                onClick={() => handleLanguageOpen(langEntry, mainPageTab === 1)}
                              >
                                {mainPageTab === 1 ? 'Open in Current Place' : 'Open'}
                              </Button>
                              {mainPageTab === 0 && ( // Only show delete in Main ICF tab
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleLanguageDelete(langEntry._id)}
                                  disabled={languagesLoading}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {languagesError && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2, color: 'error.main' }}>
                          {languagesError}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {mainPageTab === 0 && editorTab === 0 && (
          <>
            {selectedLanguage && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {isApprovedTranslation ? (
                  <>Viewing approved translation in <strong>{selectedLanguage}</strong>. Version: {selectedVersion || 'Latest'}</>
                ) : forReview ? (
                  <>Reviewing translation in <strong>{selectedLanguage}</strong>. English version is read-only. Version: {selectedVersion || 'Latest'}</>
                ) : (
                  <>Viewing ICF in <strong>{selectedLanguage}</strong> alongside English version. Version: {selectedVersion || 'Latest'}</>
                )}
              </Alert>
            )}
            {isTranslating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography>Translating sections to {selectedLanguage}...</Typography>
              </Box>
            )}
            <Grid container spacing={3}>
              {icfContentSections.map((section) => {
                const translatedSection = translatedSections.find(ts => ts._id === section._id);
                const displayOutput = selectedLanguage && translatedSection?.translatedOutput 
                  ? translatedSection.translatedOutput 
                  : section.output;
                
                return (
                  <Grid item xs={12} key={section._id}>
                    <Paper sx={{ p: 2, border: '1px solid transparent' }} className="icf-section-card tilt-3d">
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main' }}>
                          Section: {section.title}
                        </Typography>
                        {selectedLanguage && translatedSection?.translationFailed && (
                          <Tooltip title={translatedSection.translationError || 'Translation failed for this section. Showing original English content instead.'}>
                            <Chip
                              icon={<WarningAmberIcon />}
                              label={`Not translated to ${selectedLanguage}`}
                              color="warning"
                              size="small"
                              sx={{ ml: 2 }}
                            />
                          </Tooltip>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                    {!isApprovedTranslation && (
                      <>
                        <Tooltip title="Copy Section">
                          <IconButton size="small"><CopyIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title="Version History">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewVersionHistory(section._id)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Options">
                          <IconButton size="small" onClick={(event) => handleMenuOpen(event, section._id)}>
                            <MoreIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>

                  <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                    {selectedLanguage && translatedSection && isApprovedTranslation && !forReview ? (
                      // Approved translation: show only translated content, no English
                      <>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              {selectedLanguage} Version
                            </Typography>
                          </Box>
                          <Box className="icf-preview-box" sx={{ 
                            minHeight: 200, 
                            bgcolor: '#f8f9fa', 
                            border: '2px solid rgba(0, 0, 0, 0.2)', 
                            borderRadius: 3, 
                            p: 3, 
                            fontFamily: 'Times New Roman, serif',
                            fontSize: 14,
                            color: '#333333',
                            lineHeight: 1.6,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {translatedSection.translatedOutput && translatedSection.translatedOutput.trim() ? (
                              <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(translatedSection.translatedOutput) }} />
                            ) : (
                              <Box sx={{ 
                                color: '#999',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: 4
                              }}>
                                [No translated content available for this section]
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </>
                    ) : selectedLanguage && translatedSection ? (
                      <>
                        {/* English Version */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              English Version {forReview && <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>(Read-only)</Typography>}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Show accuracy toggle and badge if accuracy data exists */}
                              {retranslatedSections[section._id] && retranslatedSections[section._id].accuracy !== undefined && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setShowAccuracy(prev => ({
                                      ...prev,
                                      [section._id]: !prev[section._id]
                                    }))}
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      px: 1
                                    }}
                                  >
                                    {showAccuracy[section._id] ? 'Hide' : 'Show'} Accuracy
                                  </Button>
                                  {showAccuracy[section._id] && (
                                    <>
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        bgcolor: retranslatedSections[section._id].accuracy >= 80 ? '#4caf50' : 
                                                 retranslatedSections[section._id].accuracy >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem'
                                      }}>
                                        <Box sx={{ 
                                          width: 12, 
                                          height: 12, 
                                          borderRadius: '50%', 
                                          bgcolor: 'white',
                                          animation: retranslatedSections[section._id].accuracy >= 80 ? 'pulse 2s infinite' : 'none',
                                          '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.5 }
                                          }
                                        }} />
                                        {retranslatedSections[section._id].accuracy.toFixed(1)}% Accurate
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9, ml: 0.5 }}>
                                          ({accuracyMethod[section._id] === 'rule-based' ? 'Rule' : 'LLM'})
                                        </Typography>
                                      </Box>
                                    </>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                          <Box
                            ref={(node) => registerEnglishPreviewBox(section._id, node)}
                            className="icf-preview-box"
                            sx={{ 
                            minHeight: 200, 
                            bgcolor: '#f8f9fa', 
                            border: '2px solid rgba(0, 0, 0, 0.2)', 
                            borderRadius: 3, 
                            p: 3, 
                            fontFamily: 'Times New Roman, serif',
                            fontSize: 14,
                            color: '#333333',
                            lineHeight: 1.6,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {section.output && section.output.trim() ? (
                              <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(section.output) }} />
                            ) : (
                              <Box sx={{ 
                                color: '#999',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: 4
                              }}>
                                [No content available for this section]
                              </Box>
                            )}
                          </Box>
                        </Grid>
                        {/* Translated Version / Re-translated English */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              {showRetranslatedText[section._id] ? 'Re-translated English' : `${selectedLanguage} Version`}
                              {forReview && <Typography component="span" variant="caption" sx={{ color: 'primary.main', ml: 1, fontWeight: 500 }}>(Editable)</Typography>}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Toggle button to show/hide accuracy (only show if accuracy data exists) */}
                              {retranslatedSections[section._id] && retranslatedSections[section._id].accuracy !== undefined && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setShowAccuracy(prev => ({
                                    ...prev,
                                    [section._id]: !prev[section._id]
                                  }))}
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    px: 1
                                  }}
                                >
                                  {showAccuracy[section._id] ? 'Hide' : 'Show'} Accuracy
                                </Button>
                              )}
                              
                              {/* Toggle between retranslated text and language version */}
                              {retranslatedSections[section._id] && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setShowRetranslatedText(prev => ({
                                    ...prev,
                                    [section._id]: !prev[section._id]
                                  }))}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {showRetranslatedText[section._id] ? `Show ${selectedLanguage} Version` : 'Show Re-translated English'}
                                </Button>
                              )}
                              
                              {/* Accuracy Method Selector and Check/Switch buttons */}
                              {retranslatedSections[section._id] ? (
                                // If accuracy already exists, show switch button
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={async () => {
                                    // Switch to the other accuracy method
                                    // IMPORTANT: Uses stored accuracy if available, otherwise calculates it
                                    const currentMethod = accuracyMethod[section._id] || 'llm';
                                    const newMethod = currentMethod === 'llm' ? 'rule-based' : 'llm';
                                    setAccuracyMethod(prev => ({ ...prev, [section._id]: newMethod }));
                                    
                                    // Check if we already have accuracy for this method stored
                                    const existingData = retranslatedSections[section._id];
                                    const storedAccuracy = newMethod === 'llm' 
                                      ? existingData?.accuracyLLM 
                                      : existingData?.accuracyRuleBased;
                                    
                                    if (storedAccuracy !== undefined && storedAccuracy !== null) {
                                      // Use stored accuracy - no need to recalculate
                                      console.log(`✅ Using stored ${newMethod} accuracy: ${storedAccuracy}%`);
                                      
                                      // Update to show the stored accuracy
                                      setRetranslatedSections(prev => ({
                                        ...prev,
                                        [section._id]: {
                                          ...prev[section._id],
                                          accuracy: storedAccuracy,
                                          method: newMethod
                                        }
                                      }));
                                      return; // Exit early - no calculation needed
                                    }
                                    
                                    // If not stored, calculate it now
                                    console.log(`🔄 Calculating ${newMethod} accuracy (not stored yet)...`);
                                    
                                    // Get the section and original text
                                    const sectionData = sections.find(s => s._id === section._id);
                                    const originalText = sectionData?.output || '';
                                    
                                    // Use the already-retranslated text from state (or fetch from DB if not in state)
                                    let retranslatedText = retranslatedSections[section._id]?.text;
                                    
                                    // If retranslated text is not in state, try to fetch from database
                                    if (!retranslatedText) {
                                      try {
                                        const savedTranslations = await documentEditorService.getICFSectionTranslations(
                                          section._id,
                                          effectiveProjectId,
                                          {
                                            language: selectedLanguage,
                                            version: selectedVersion || 'latest',
                                            country: selectedCountry || null,
                                            site: selectedSite || null,
                                          }
                                        );
                                        
                                        if (savedTranslations && savedTranslations.length > 0) {
                                          retranslatedText = savedTranslations[0].retranslatedText || '';
                                        }
                                      } catch (err) {
                                        console.warn('Could not fetch retranslated text from DB:', err);
                                      }
                                    }
                                    
                                    // If still no retranslated text, we need to re-translate first
                                    if (!retranslatedText) {
                                      alert('No retranslated text found. Please check accuracy first to generate retranslated text.');
                                      return;
                                    }
                                    
                                    let accuracy = 0;
                                    let explanation = '';
                                    let keyDifferences = [];
                                    
                                    if (newMethod === 'llm') {
                                      // Use LLM-based accuracy calculation with existing retranslated text
                                      try {
                                        const accuracyResponse = await documentEditorService.calculateTranslationAccuracy(
                                          effectiveProjectId,
                                          originalText,
                                          retranslatedText
                                        );
                                        accuracy = accuracyResponse.accuracy || 0;
                                        explanation = accuracyResponse.explanation || '';
                                        keyDifferences = accuracyResponse.keyDifferences || [];
                                      } catch (err) {
                                        // Fallback to rule-based if LLM fails
                                        accuracy = calculateTextAccuracy(originalText, retranslatedText);
                                        explanation = 'Accuracy calculated using rule-based method (LLM failed)';
                                      }
                                    } else {
                                      // Use rule-based accuracy calculation with existing retranslated text
                                      accuracy = calculateTextAccuracy(originalText, retranslatedText);
                                      explanation = 'Accuracy calculated using rule-based method (word matching and similarity)';
                                    }
                                    
                                    // Update accuracy data - store both methods
                                    setRetranslatedSections(prev => ({
                                      ...prev,
                                      [section._id]: {
                                        ...prev[section._id],
                                        text: retranslatedText, // Ensure text is stored
                                        accuracy: accuracy, // Current accuracy for display
                                        accuracyLLM: newMethod === 'llm' ? accuracy : (prev[section._id]?.accuracyLLM || null),
                                        accuracyRuleBased: newMethod === 'rule-based' ? accuracy : (prev[section._id]?.accuracyRuleBased || null),
                                        explanation: explanation,
                                        keyDifferences: keyDifferences,
                                        method: newMethod
                                      }
                                    }));
                                    
                                    // Save to database (saves current method's accuracy)
                                    // Use same context variables as when loading translations
                                    const countryContextForSave = selectedCountry || null;
                                    const siteContextForSave = selectedSite || null;
                                    try {
                                      await documentEditorService.saveTranslationAccuracy(
                                        effectiveProjectId,
                                        section._id,
                                        selectedLanguage,
                                        selectedVersion || 'latest',
                                        countryContextForSave,
                                        siteContextForSave,
                                        {
                                          accuracy: accuracy,
                                          explanation: explanation,
                                          keyDifferences: keyDifferences,
                                          retranslatedText: retranslatedText,
                                          method: newMethod // Pass the method so both accuracies are stored separately
                                        }
                                      );
                                    } catch (saveError) {
                                      console.error('Error saving accuracy:', saveError);
                                    }
                                  }}
                                  sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                >
                                  Switch to {(accuracyMethod[section._id] || 'llm') === 'llm' ? 'Rule-based' : 'LLM'}
                                </Button>
                              ) : (
                                <>
                                  {/* Method selector before checking accuracy */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                      Method:
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant={accuracyMethod[section._id] === 'rule-based' ? 'contained' : 'outlined'}
                                      onClick={() => setAccuracyMethod(prev => ({ ...prev, [section._id]: 'rule-based' }))}
                                      sx={{ fontSize: '0.65rem', minWidth: 'auto', px: 0.75, py: 0.25 }}
                                    >
                                      Rule
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={!accuracyMethod[section._id] || accuracyMethod[section._id] === 'llm' ? 'contained' : 'outlined'}
                                      onClick={() => setAccuracyMethod(prev => ({ ...prev, [section._id]: 'llm' }))}
                                      sx={{ fontSize: '0.65rem', minWidth: 'auto', px: 0.75, py: 0.25 }}
                                    >
                                      LLM
                                    </Button>
                                  </Box>
                                  {/* Check Accuracy button */}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<TranslateIcon />}
                                    onClick={() => handleRetranslateToEnglish(section._id, translatedSection.translatedOutput)}
                                    disabled={isRetranslating[section._id] || !translatedSection?.translatedOutput}
                                    sx={{ fontSize: '0.75rem' }}
                                  >
                                    {isRetranslating[section._id] ? (
                                      <>
                                        <CircularProgress size={14} sx={{ mr: 1 }} />
                                        Re-translating...
                                      </>
                                    ) : (
                                      'Check Accuracy'
                                    )}
                                  </Button>
                                </>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                          {forReview && !isApprovedTranslation && !showRetranslatedText[section._id] ? (
                            // Editable TextField for review mode (only if not approved)
                            <>
                              <TextField
                                fullWidth
                                multiline
                                minRows={8}
                                value={editedTranslationContent[section._id] !== undefined ? editedTranslationContent[section._id] : (() => {
                                  try {
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = translatedSection.translatedOutput || '';
                                    return tempDiv.textContent || tempDiv.innerText || '';
                                  } catch (error) {
                                    console.error('Error extracting translation text:', error);
                                    return translatedSection.translatedOutput || '';
                                  }
                                })()}
                                onChange={(e) => {
                                  setEditedTranslationContent(prev => ({
                                    ...prev,
                                    [section._id]: e.target.value
                                  }));
                                }}
                                variant="outlined"
                                size="small"
                                placeholder="Edit translated content here..."
                                helperText="Edit the translated content. HTML formatting will be preserved when saved."
                                className="icf-response-field"
                                sx={translatedTextFieldSx(section._id)}
                              />
                              {editedTranslationContent[section._id] !== undefined && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={async () => {
                                      const html = convertPlainTextToHtml(editedTranslationContent[section._id] || '');
                                      try {
                                        await documentEditorService.updateTranslation(
                                          effectiveProjectId,
                                          section._id,
                                          selectedLanguage,
                                          selectedVersion || 'latest',
                                          selectedCountry || null,
                                          selectedSite || null,
                                          html
                                        );
                                        // Refresh translated sections
                                        const updatedTranslatedSection = { ...translatedSection, translatedOutput: html };
                                        setTranslatedSections(prev => prev.map(ts => ts._id === section._id ? updatedTranslatedSection : ts));
                                        setEditedTranslationContent(prev => {
                                          const copy = { ...prev };
                                          delete copy[section._id];
                                          return copy;
                                        });
                                      } catch (error) {
                                        console.error('Error saving translation:', error);
                                        alert(error?.message || 'Failed to save translation. Please try again.');
                                      }
                                    }}
                                    className="icf-btn-primary btn-3d"
                                  >
                                    Save Translation
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                      setEditedTranslationContent(prev => {
                                        const copy = { ...prev };
                                        delete copy[section._id];
                                        return copy;
                                      });
                                    }}
                                  >
                                    Discard
                                  </Button>
                                </Box>
                              )}
                            </>
                          ) : (
                            // Preview box for non-review mode
                            <Box className="icf-preview-box" sx={{ 
                              ...getTranslatedPanelHeightSx(section._id),
                              overflowY: 'auto',
                              minHeight: 200, 
                              bgcolor: (showRetranslatedText[section._id] && retranslatedSections[section._id]) ? '#e8f5e9' : '#f0f7ff', 
                              border: (showRetranslatedText[section._id] && retranslatedSections[section._id] && retranslatedSections[section._id].accuracy !== undefined) ? 
                                `2px solid ${retranslatedSections[section._id].accuracy >= 80 ? '#4caf50' : 
                                           retranslatedSections[section._id].accuracy >= 60 ? '#ff9800' : '#f44336'}` : 
                                '2px solid rgba(25, 118, 210, 0.3)', 
                              borderRadius: 3, 
                              p: 3, 
                              fontFamily: 'Times New Roman, serif',
                              fontSize: 14,
                              color: '#333333',
                              lineHeight: 1.6,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                              '& h1, & h2, & h3, & h4, & h5, & h6': {
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                mt: 2,
                                mb: 1
                              },
                              '& h1': { fontSize: '1.5em' },
                              '& h2': { fontSize: '1.3em' },
                              '& h3': { fontSize: '1.1em' },
                              '& p': { mb: 1 },
                              '& ul, & ol': { pl: 2, mb: 1 },
                              '& li': { mb: 0.5 },
                              '& table': { 
                                borderCollapse: 'collapse',
                                width: '100%',
                                mb: 2
                              },
                              '& th, & td': {
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'left'
                              },
                              '& th': {
                                backgroundColor: (showRetranslatedText[section._id] && retranslatedSections[section._id]) ? '#e8f5e9' : '#f0f7ff',
                                fontWeight: 'bold'
                              }
                            }}>
                              {showRetranslatedText[section._id] && retranslatedSections[section._id] ? (
                                // When showing re-translated English, also highlight differences vs original English
                                <Box
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeRichHtml(buildHighlightedRetranslatedHtml(
                                      section.output || '',
                                      retranslatedSections[section._id].text || '',
                                      section._id
                                    )),
                                  }}
                                />
                              ) : translatedSection.translatedOutput && translatedSection.translatedOutput.trim() ? (
                                <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(translatedSection.translatedOutput) }} />
                              ) : (
                                <Box
                                  sx={{
                                    color: '#999',
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                    py: 4,
                                  }}
                                >
                                  [Translation not available]
                                </Box>
                              )}
                            </Box>
                          )}
                          </Box>
                        </Grid>
                      </>
                    ) : isApprovedTranslation && !forReview && selectedLanguage ? (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
                          {isTranslating || isLoading ? (
                            <>
                              <CircularProgress size={22} />
                              <Typography color="text.secondary">Loading approved translation…</Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary" fontStyle="italic">
                              [No translated content available for this section]
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                          {mode === 'edit' ? 'Generated Content' : 'Document Preview'}
                        </Typography>
                        {mode === 'edit' ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={18}
                            value={editedTextContent[section._id] || (() => {
                              try {
                                const tempDiv = document.createElement('div');
                                tempDiv.innerHTML = section.output || '';
                                return tempDiv.textContent || tempDiv.innerText || '';
                              } catch (error) {
                                console.error('Error extracting text content:', error);
                                return section.output || '';
                              }
                            })()}
                            onChange={(e) => {
                              handleTextContentChange(section._id, 'output', e.target.value);
                            }}
                            variant="outlined"
                            size="small"
                            placeholder="Edit text content here (HTML tags are automatically preserved)..."
                            helperText="Only the text content can be edited. HTML formatting is protected."
                            className="icf-response-field"
                          />
                        ) : (
                          <Box className="icf-preview-box" sx={{ 
                            minHeight: 200, 
                            bgcolor: '#f8f9fa', 
                            border: '2px solid rgba(0, 0, 0, 0.2)', 
                            borderRadius: 3, 
                            p: 3, 
                            fontFamily: 'Times New Roman, serif',
                            fontSize: 14,
                            color: '#333333',
                            lineHeight: 1.6,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {displayOutput && displayOutput.trim() ? (
                              <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(displayOutput) }} />
                            ) : (
                              <Box sx={{ 
                                color: '#999',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: 4
                              }}>
                                [No content available for this section]
                              </Box>
                            )}
                          </Box>
                        )}
                        {mode === 'edit' && editedTextContent[section._id] !== undefined && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveSectionContent(section._id)}
                              disabled={isGenerating[section._id]}
                              className="icf-btn-primary btn-3d"
                            >
                              Save Changes
                            </Button>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() =>
                                setEditedTextContent((prev) => {
                                  const copy = { ...prev };
                                  delete copy[section._id];
                                  return copy;
                                })
                              }
                            >
                              Discard
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    )}

                    {!(isApprovedTranslation && !forReview) && mode === 'edit' && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={10}
                            value={getUserEditPrompt(section._id)}
                            onChange={(e) => setUserEditPrompt(section._id, e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder=""
                            disabled={section.final || isGenerating[section._id]}
                            className="icf-prompt-field"
                            sx={{ flex: 1 }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              minWidth: 180,
                              justifyContent: 'space-between',
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                section.final ||
                                isGenerating[section._id] ||
                                !getUserEditPrompt(section._id).trim()
                              }
                              onClick={() => handleGenerateContent(section._id)}
                              className="icf-btn-primary btn-3d"
                            >
                              {isGenerating[section._id] ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                'Generate Content'
                              )}
                            </Button>
                            <Button
                              variant={section.final ? 'outlined' : 'contained'}
                              color={section.final ? 'success' : 'primary'}
                              size="small"
                              disabled={section.final}
                              onClick={() => handleMarkAsFinal(section._id)}
                              className="btn-3d"
                            >
                              {section.final ? 'Section is Final' : 'Mark as Final'}
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {mode !== 'edit' && selectedCountry && (section?.output || '').trim() && !(isApprovedTranslation && !forReview) && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            startIcon={<TranslateIcon />}
                            onClick={() => openTranslateDialogForSection(section)}
                          >
                            Translate
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
                );
              })}
              
              {/* Approve button for review mode (only show if not already approved) */}
              {forReview && !isApprovedTranslation && selectedLanguage && selectedCountry && selectedSite && sections.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                      Review complete? Approve this translation to make it available in the ICF plan.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      startIcon={<CheckCircleIcon />}
                      onClick={async () => {
                        // Find the language entry ID
                        const currentLangEntry = languages.find(
                          lang => lang.language === selectedLanguage &&
                          lang.country === selectedCountry &&
                          lang.site === selectedSite &&
                          (selectedVersion ? lang.version === selectedVersion : true)
                        );
                        
                        if (!currentLangEntry) {
                          alert('Language entry not found. Cannot approve.');
                          return;
                        }
                        
                        try {
                          await documentEditorService.approveICFLanguage(currentLangEntry._id, effectiveProjectId);
                          await refreshLanguages();
                          alert('Translation approved successfully!');
                          // Navigate back to translations tab using window.location for full page reload
                          window.location.href = `/document-editor/study-form?project=${effectiveProjectId}&tab=translations`;
                        } catch (error) {
                          console.error('Error approving translation:', error);
                          alert(error?.message || 'Failed to approve translation. Please try again.');
                        }
                      }}
                      sx={{ 
                        px: 3,
                        py: 0.75,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      Approve Translation
                    </Button>
                  </Box>
                </Grid>
              )}
          </Grid>
          </>
        )}

        {mainPageTab === 0 && editorTab === 1 && (
          <>
            {!titlePageSection ? (
              <Paper sx={{ p: 3, border: '1px solid transparent' }} className="icf-section-card tilt-3d">
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
                  Title Page
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  The ICF title page identifies the study and document with administrative and study identification information only.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={generateTitlePageSection}
                  disabled={isGenerating.titlePage || !effectiveProjectId}
                  className="icf-btn-primary btn-3d"
                >
                  {isGenerating.titlePage ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Generate Title Page'}
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {[titlePageSection].map((section) => {
                  const translatedSection = translatedSections.find(ts => ts._id === section._id);
                  const displayOutput = selectedLanguage && translatedSection?.translatedOutput
                    ? translatedSection.translatedOutput
                    : section.output;

                  return (
                    <Grid item xs={12} key={section._id}>
                      <Paper sx={{ p: 2, border: '1px solid transparent' }} className="icf-section-card tilt-3d">
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main' }}>
                            Title Page
                          </Typography>
                          <Box sx={{ flexGrow: 1 }} />
                          {!isApprovedTranslation && (
                            <>
                              <Tooltip title="Version History">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewVersionHistory(section._id)}
                                >
                                  <HistoryIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="More Options">
                                <IconButton size="small" onClick={(event) => handleMenuOpen(event, section._id)}>
                                  <MoreIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                              {mode === 'edit' ? 'Generated Content' : 'Document Preview'}
                            </Typography>
                            {mode === 'edit' ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={18}
                                value={editedTextContent[section._id] || (() => {
                                  try {
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = section.output || '';
                                    return tempDiv.textContent || tempDiv.innerText || '';
                                  } catch (error) {
                                    return section.output || '';
                                  }
                                })()}
                                onChange={(e) => handleTextContentChange(section._id, 'output', e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Edit title page content here..."
                                helperText="Only the text content can be edited. HTML formatting is protected."
                                className="icf-response-field"
                              />
                            ) : (
                              <Box className="icf-preview-box" sx={{
                                minHeight: 200,
                                bgcolor: '#f8f9fa',
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                borderRadius: 3,
                                p: 3,
                                fontFamily: 'Times New Roman, serif',
                                fontSize: 14,
                                color: '#333333',
                                lineHeight: 1.6,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                  fontWeight: 'bold',
                                  color: '#2c3e50',
                                  mt: 2,
                                  mb: 1,
                                },
                                '& h1': { fontSize: '1.5em' },
                                '& h2': { fontSize: '1.3em' },
                                '& h3': { fontSize: '1.1em' },
                                '& p': { mb: 1 },
                                '& table': {
                                  borderCollapse: 'collapse',
                                  width: '100%',
                                  mb: 2,
                                },
                                '& th, & td': {
                                  border: '1px solid #ddd',
                                  padding: '8px',
                                  textAlign: 'left',
                                },
                                '& th': {
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: 'bold',
                                },
                              }}>
                                {displayOutput && displayOutput.trim() ? (
                                  <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(displayOutput) }} />
                                ) : (
                                  <Box sx={{ color: '#999', fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                    [No title page content available]
                                  </Box>
                                )}
                              </Box>
                            )}
                            {mode === 'edit' && editedTextContent[section._id] !== undefined && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleSaveSectionContent(section._id)}
                                  disabled={isGenerating[section._id]}
                                  className="icf-btn-primary btn-3d"
                                >
                                  Save Changes
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() =>
                                    setEditedTextContent((prev) => {
                                      const copy = { ...prev };
                                      delete copy[section._id];
                                      return copy;
                                    })
                                  }
                                >
                                  Discard
                                </Button>
                              </Box>
                            )}
                          </Grid>

                          {!(isApprovedTranslation && !forReview) && mode === 'edit' && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  maxRows={10}
                                  value={getUserEditPrompt(section._id)}
                                  onChange={(e) => setUserEditPrompt(section._id, e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  placeholder=""
                                  className="icf-prompt-field"
                                  disabled={section.final || isGenerating[section._id]}
                                  sx={{ flex: 1 }}
                                />
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    minWidth: 180,
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    disabled={
                                      section.final ||
                                      isGenerating[section._id] ||
                                      !getUserEditPrompt(section._id).trim()
                                    }
                                    onClick={() => handleGenerateContent(section._id)}
                                    className="icf-btn-primary btn-3d"
                                  >
                                    {isGenerating[section._id] ? (
                                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    ) : (
                                      'Generate Content'
                                    )}
                                  </Button>
                                  <Button
                                    variant={section.final ? 'outlined' : 'contained'}
                                    color={section.final ? 'success' : 'primary'}
                                    size="small"
                                    disabled={section.final}
                                    onClick={() => handleMarkAsFinal(section._id)}
                                    className="btn-3d"
                                  >
                                    {section.final ? 'Section is Final' : 'Mark as Final'}
                                  </Button>
                                </Box>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        <Dialog open={isCountryDialogOpen} onClose={closeCountryDialog} fullWidth maxWidth="sm">
          <DialogTitle>Add Country-Specific ICF</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Enter a country name to create a localized copy of the current ICF. The editor will open with the latest content so you can tailor it for that region.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="icf-country-clone-source-label">Clone content from</InputLabel>
              <Select
                labelId="icf-country-clone-source-label"
                label="Clone content from"
                value={countryCloneSource}
                onChange={(event) => setCountryCloneSource(event.target.value)}
              >
                <MenuItem value="base">Main ICF (latest)</MenuItem>
                {countryEntries.map((entry) => {
                  const key = buildCountryKey(entry.name);
                  const latestVersion =
                    entry.latestVersionLabel || buildInitialCountryVersion(entry.name);
                  return (
                    <MenuItem key={key} value={key}>
                      {entry.name} — {latestVersion}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {countrySelectionOptions && countrySelectionOptions.length > 0 && (
              <FormControl fullWidth size="small" sx={{ mb: countryNameMode === 'custom' ? 2 : 0 }}>
                <InputLabel id="icf-country-from-feasibility-label">Country</InputLabel>
                <Select
                  labelId="icf-country-from-feasibility-label"
                  label="Country"
                  value={countryNameMode === 'selected' ? (newCountryName || '') : '__custom__'}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === '__custom__') {
                      setCountryNameMode('custom');
                      setNewCountryName('');
                    } else {
                      setCountryNameMode('selected');
                      setNewCountryName(String(value));
                      if (countryNameError) setCountryNameError('');
                    }
                  }}
                >
                  {countrySelectionOptions.map((name) => (
                    <MenuItem key={`icf-country-opt-${name}`} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                  <MenuItem value="__custom__">Custom…</MenuItem>
                </Select>
              </FormControl>
            )}

            {(countryNameMode === 'custom' || !(countrySelectionOptions && countrySelectionOptions.length > 0)) && (
              <TextField
                autoFocus
                fullWidth
                label="Country name"
                value={newCountryName}
                onChange={(event) => {
                  setNewCountryName(event.target.value);
                  if (countryNameError) setCountryNameError('');
                }}
                error={Boolean(countryNameError)}
                helperText={countryNameError || 'Example: United States, France, Japan'}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCountryDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleCountrySubmit}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isSiteDialogOpen} onClose={closeSiteDialog} fullWidth maxWidth="sm">
          <DialogTitle>Add Site-Specific ICF</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Provide the country and site / hospital name to generate a localized copy using the latest country-specific ICF. Site versions are tracked independently.
            </Typography>
            <TextField
              fullWidth
              label="Country"
              value={siteCountryForDialog}
              onChange={(event) => setSiteCountryForDialog(event.target.value)}
              sx={{ mb: 2 }}
              helperText="Defaults to India when blank."
            />
            <TextField
              autoFocus
              fullWidth
              label="Site / hospital name"
              value={newSiteName}
              onChange={(event) => {
                setNewSiteName(event.target.value);
                if (siteNameError) setSiteNameError('');
              }}
              error={Boolean(siteNameError)}
              helperText={siteNameError || 'Example: Apollo Hospital, City Clinic'}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSiteDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSiteSubmit} disabled={countryEntries.length === 0}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Language Dialog - Step by Step */}
        <Dialog open={isLanguageDialogOpen} onClose={closeLanguageDialog} fullWidth maxWidth="sm">
          <DialogTitle>Add Language Translation</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Select country, site, language, and version to create a translation entry. The ICF will be translated to the selected language when you open it.
            </Typography>
            
            {/* Step 1: Select Country */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="country-select-label">Country *</InputLabel>
              <Select
                labelId="country-select-label"
                label="Country *"
                value={selectedCountryForLanguage}
                onChange={(event) => {
                  handleCountrySelectForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {countryEntries.map((entry) => (
                  <MenuItem key={entry.countryKey || entry.name} value={entry.name}>
                    {entry.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Step 2: Select Site */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedCountryForLanguage}>
              <InputLabel id="site-select-label">Site *</InputLabel>
              <Select
                labelId="site-select-label"
                label="Site *"
                value={selectedSiteForLanguage}
                onChange={(event) => {
                  handleSiteSelectForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {availableSitesForLanguage.map((entry) => (
                  <MenuItem key={`${entry.country}-${entry.site}`} value={entry.site}>
                    {entry.site}
                  </MenuItem>
                ))}
              </Select>
              {!selectedCountryForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a country first
                </Typography>
              )}
            </FormControl>

            {/* Step 3: Select Language */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedSiteForLanguage}>
              <InputLabel id="language-select-label">Language *</InputLabel>
              <Select
                labelId="language-select-label"
                label="Language *"
                value={newLanguage}
                onChange={(event) => {
                  setNewLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {SUPPORTED_TRANSLATION_LANGUAGES.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
                <MenuItem value="Custom">Custom…</MenuItem>
              </Select>
              {!selectedSiteForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a site first
                </Typography>
              )}
            </FormControl>
            
            {newLanguage === 'Custom' && (
              <TextField
                fullWidth
                label="Custom Language"
                placeholder="Enter language name"
                value={customLanguageText}
                onChange={(event) => {
                  setCustomLanguageText(event.target.value);
                  if (languageError) setLanguageError('');
                }}
                sx={{ mb: 2 }}
                helperText="Enter the language name (e.g., Hindi, Arabic, Russian)"
              />
            )}

            {/* Step 4: Select Version */}
            <FormControl fullWidth disabled={!selectedSiteForLanguage}>
              <InputLabel id="version-select-label">Version *</InputLabel>
              <Select
                labelId="version-select-label"
                label="Version *"
                value={selectedVersionForLanguage}
                onChange={(event) => {
                  setSelectedVersionForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {availableVersionsForLanguage.map((version) => (
                  <MenuItem key={version} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </Select>
              {!selectedSiteForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a site first
                </Typography>
              )}
            </FormControl>
            
            {languageError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {languageError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLanguageDialog} disabled={languagesLoading}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleLanguageSubmit}
              disabled={languagesLoading || !selectedCountryForLanguage || !selectedSiteForLanguage || !newLanguage || !selectedVersionForLanguage}
            >
              {languagesLoading ? <CircularProgress size={20} /> : 'Add Language'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={translateState.open}
          onClose={translateState.loading ? undefined : closeTranslateDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Translate “{translateState.sectionTitle || 'ICF Section'}”
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Preview localized text without overwriting the original English content. Editing remains in English.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel id="icf-translate-language-label">Target language</InputLabel>
                <Select
                  labelId="icf-translate-language-label"
                  label="Target language"
                  value={translateState.language}
                  onChange={(event) =>
                    setTranslateState((prev) => ({
                      ...prev,
                      language: event.target.value,
                      error: null,
                    }))
                  }
                >
                  {SUPPORTED_TRANSLATION_LANGUAGES.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                  <MenuItem value="Custom">Custom…</MenuItem>
                </Select>
              </FormControl>
              {translateState.language === 'Custom' && (
                <TextField
                  label="Custom language"
                  size="small"
                  value={translateState.customLanguage}
                  onChange={(event) =>
                    setTranslateState((prev) => ({
                      ...prev,
                      customLanguage: event.target.value,
                      error: null,
                    }))
                  }
                />
              )}
            </Box>
            {translateState.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {translateState.error}
              </Alert>
            )}
            {translateState.translation && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: '#fafbff',
                  maxHeight: 360,
                  overflowY: 'auto',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Preview ({resolveTargetLanguage()})
                </Typography>
                <Box
                  sx={{
                    fontFamily: 'Times New Roman, serif',
                    fontSize: 15,
                    color: 'text.primary',
                    lineHeight: 1.6,
                    '& h1, & h2, & h3': { mt: 2, mb: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(translateState.translation) }}
                />
              </Paper>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTranslateDialog} disabled={translateState.loading}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handlePerformTranslation}
              startIcon={!translateState.loading && <TranslateIcon />}
              disabled={translateState.loading}
            >
              {translateState.loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Translate'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => handleDeleteSection(selectedSectionId)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Section</ListItemText>
          </MenuItem>
        </Menu>

        {/* Scroll to Top Button */}
        <ScrollToTop />
      </Box>
    </ThemeProvider>
  );
};

export default ICFEditor; 